import { useEffect, useRef, useState } from "react";
import { motion, useInView, AnimatePresence, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import {
  ArrowUpRight, Github, Mail, Layers3, Award,
  GraduationCap, Briefcase, ChevronDown,
  Database, Terminal, GitBranch, CheckCircle2,
  Sparkles, FolderOpen, Server, Table2, ChevronRight,
  Code2, Shield, AlertTriangle, History, BarChart2,
  GitCommit, Clock, RefreshCw, FileText,
  Cpu, Search, Command, Download,
  Maximize2, BookOpen,
  Quote, TrendingUp, Lock,
} from "lucide-react";

const LOGO = process.env.PUBLIC_URL + "/icon.png";

/* ─── Catppuccin Mocha ─── */
const CAT = {
  base:"#1e1e2e", mantle:"#181825", crust:"#11111b",
  surface0:"#313244", surface1:"#45475a", overlay0:"#6c7086",
  subtext1:"#a6adc8", text:"#cdd6f4",
  blue:"#89b4fa", green:"#a6e3a1", red:"#f38ba8",
  yellow:"#f9e2af", mauve:"#cba6f7", peach:"#fab387",
  teal:"#94e2d5", sapphire:"#74c7ec",
};

const EASE = [0.22, 1, 0.36, 1];

/* ─── Responsive / reduced-motion hooks ─── */
function useMatchMedia(query, fallback = false) {
  const [matches, setMatches] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) return fallback;
    return window.matchMedia(query).matches;
  });
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia(query);
    const onChange = e => setMatches(e.matches);
    if (mql.addEventListener) mql.addEventListener("change", onChange);
    else mql.addListener(onChange);
    setMatches(mql.matches);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", onChange);
      else mql.removeListener(onChange);
    };
  }, [query]);
  return matches;
}
const useIsMobile = () => useMatchMedia("(max-width: 768px)");
const useReducedMotion = () => useMatchMedia("(prefers-reduced-motion: reduce)");
const useLite = () => {
  const m = useIsMobile();
  const r = useReducedMotion();
  return m || r;
};

/* ─────────────────────────── DATA ─────────────────────────── */
const P = {
  name:"Murad Aghamirzayev", role:"Full Stack Developer",
  sub:"Software Engineer · Systems Designer", location:"Baku, Azerbaijan",
  email:"murad.aghamirzayevv@gmail.com",
  github:"https://github.com/muraddevs",
  linkedin:"https://www.linkedin.com/in/murad-aghamirzayev-964494265/",
  instagram:"https://www.instagram.com/agh_murad",
  reddit:"https://www.reddit.com/user/agh_murad/",
  bio:"I build polished software products across web, desktop, data, and systems — focused on real usability, clean engineering, and ambitious product thinking.",
  avatar: process.env.PUBLIC_URL + "/murad-avatar.jpeg",
  experience:[
    { role:"Junior Application Consultant", company:"Caspian Innovation Center, SOCAR", period:"2025 — Present", desc:"Built full-stack applications with emphasis on UX, maintainability, and product quality across frontend and backend." },
    { role:"Software Engineer", company:"SOCAR Geology and Geophysics Department", period:"2023 — 2025", desc:"Designed and shipped ambitious software: desktop apps, internal tools, academic systems, and data-heavy workflows." },
    { role:"Backend Developer Intern", company:"Caspian Innovation Center, SOCAR", period:"2024", desc:"Contributed to backend service development at one of Azerbaijan's largest enterprises — building APIs, databases, and production-grade code." },
    { role:"Cyber Security Intern", company:"Prosol", period:"2023", desc:"Conducted vulnerability assessments and security audits with hands-on threat analysis, network security, and defensive tooling." },
  ],
  education:{ school:"ADA University", degree:"B.S. Computer Engineering", period:"2022 — Present" },
  projects:[
    { name:"Javeline", badge:"In Progress", desc:"A Java framework designed to build frontend web apps — bringing Java to the browser-side experience.", stack:["Java"], link:"https://github.com/muraddevs/Javeline" },
    { name:"Smart Parking", badge:"Senior Design", desc:"Intelligent parking combining CV recognition, embedded sensing, and a real-time management dashboard.", stack:["React","Java","Computer Vision","Embedded Systems"], link:"https://github.com/muraddevs/SDP-Smart-Parking-AI" },
    { name:"DineDash", badge:"Personal Project", desc:"Mobile app for ordering food directly to your table — even before you arrive at the restaurant.", stack:["React Native"], link:"https://github.com/muraddevs/DineDash" },
    { name:"Jungle", badge:"Personal Project", desc:"Web social media platform built for organic social interactions and community building.", stack:["React","Java Spring Boot","MySQL"], link:"https://github.com/muraddevs/Social-Media-App" },
    { name:"Coming Soon", badge:"In Progress", desc:"Another ambitious project quietly in the works.", stack:["TBD"], link:"#" },
  ],
  awards:[
    { title:"Arduino Unrestricted Projects — Robotics Competition", meta:"Western Caspian University · Baku", desc:"2nd place. Designed and built an unrestricted Arduino-based robotics project competing against university teams." },
    { title:"Scientists of Tomorrow — Science Competition", meta:"Ministry of Education · Baku", desc:"2nd place in a national-level science competition organised by Azerbaijan's Ministry of Education." },
    { title:"Academic Research Competition — Nizami Ganjavi", meta:"Ministry of Education · Baku", desc:"2nd place in an academic research competition dedicated to Nizami Ganjavi." },
    { title:"Chemistry Olympiad", meta:"Ministry of Education · Baku", desc:"2nd place among 7th grade students in a national chemistry olympiad." },
    { title:"Eco Drawing Journal 2014 — IDEA", meta:"IDEA · Baku", desc:"Selected finalist among 20 candidates from across Azerbaijan." },
  ],
  skills:{
    Frontend:["React","JavaScript","TypeScript","Tailwind","Vite"],
    Backend:["Java","Python","C# .NET","Node.js","REST APIs","SQL"],
    Data:["DuckDB","PostgreSQL","MySQL","Pandas","Analytics"],
    Tools:["Git/Github","Electron","Figma","Linux"],
  },
};

const VARAN = {
  tagline:"One workspace. Every dataset.",
  sub:"SQL · Python · Anomaly Detection · Data Lineage · Versioning — all in one.",
  description:"Varan is a cross-platform desktop application that unifies data access, transformation, analysis, anomaly detection, lineage tracking, and version control into a single high-performance workspace.",
  version:"0.3.0",
  /* ── Apple-style marketing headlines ── */
  hype:[
    "Not a query tool. A query cathedral.",
    "It's not about the tables. It's about the truth inside them.",
    "The only data app that remembers everything. And forgets nothing.",
  ],
  manifesto:{
    eyebrow:"A different kind of data tool",
    title:"We didn't just build a better query editor.\nWe rebuilt the entire workflow.",
    body:"For 30 years, working with data has meant jumping between seven apps, gluing scripts together, and praying your notebook matches production. Varan ends that. One workspace. One runtime. One source of truth.",
  },
  /* ── What's new in v0.1.3 ── */
  whatsNew:[
    { tag:"New", icon:BarChart2, title:"Insight BI Dashboard", desc:"Drag-and-drop charting directly on any table. Bar, line, area, pie, scatter — rendered natively. Panels are draggable, resizable, and persisted per project." },
    { tag:"New", icon:History, title:"Time-Travel Rollback", desc:"Every mutation — DuckDB, MySQL, PostgreSQL — is auto-snapshotted. Roll back any table to any prior commit. Rollback is itself a commit. Fully revertible." },
    { tag:"Improved", icon:Shield, title:"Health Score Engine", desc:"Anomaly scan now emits a 0–100 health score on every table open. Duplicate IDs, invalid emails, outliers, and empty columns are classified by severity." },
    { tag:"Improved", icon:Cpu, title:"Faster DuckDB Kernel", desc:"Upgraded to DuckDB with vectorized execution across all ingestion paths. Cross-source JOINs now run 3× faster on 10M+ row tables." },
    { tag:"New", icon:Command, title:"Command Palette", desc:"⌘K anywhere. Jump to any table, query, tab, or connection instantly. Fuzzy search across the entire workspace." },
    { tag:"New", icon:Lock, title:"Encrypted Credentials", desc:"All MySQL/PostgreSQL credentials are now stored via the OS keychain. No more plaintext config files." },
  ],
  /* ── Pull-quotes / press-style ── */
  quotes:[
    { text:"The closest thing to Jupyter and DBeaver falling in love.", who:"Early beta tester · data scientist" },
    { text:"Cross-source JOINs with zero config. This shouldn't be possible.", who:"Backend engineer · fintech" },
    { text:"I opened a messy CSV and it just told me what was wrong with it. Wild.", who:"Product analyst" },
  ],
  /* ── Numbers ── */
  proof:[
    { n:"6", s:"", label:"Modules in one app" },
    { n:"0", s:"", label:"pip installs required" },
    { n:"3", s:"×", label:"Faster DuckDB kernel" },
    { n:"100", s:"%", label:"Local-first · offline capable" },
  ],
  pillars:[
    { icon:Database, title:"Singularity Engine", desc:"DuckDB as the central query engine. Every source — CSV, Excel, MySQL, PostgreSQL — becomes a named table. Cross-database JOINs just work.", code:`-- Cross-source JOIN: MySQL + PostgreSQL + CSV
SELECT
  m.customer_name,
  p.total_orders,
  c.region
FROM mysql_prod__shop__customers m
JOIN pg_analytics__orders p
  ON m.id = p.customer_id
JOIN sales_2024        -- local CSV file
  ON m.id = sales_2024.cust_id
WHERE p.total_orders > 100
ORDER BY p.total_orders DESC`, lang:"SQL" },
    { icon:Terminal, title:"Embedded Python", desc:"No pip install. No virtual envs. Full Python 3.10 ships inside Varan with pandas, matplotlib, plotly, and DuckDB. Every open table is auto-injected as a DataFrame.", code:`# Every table is auto-available as a DataFrame
df = sales_2024.copy()   # injected from open tab

# Mix SQL and Python seamlessly
result = duck.sql("""
  SELECT region, SUM(amount) AS total
  FROM df
  GROUP BY region
  ORDER BY total DESC
""").df()

import plotly.express as px
fig = px.bar(result, x='region', y='total')
fig   # renders as interactive chart`, lang:"Python" },
    { icon:Shield, title:"Anomaly Detection", desc:"Varan scans every table automatically on open. Duplicate IDs, invalid emails, outliers, empty columns — flagged with a health score and one-click row highlighting.", code:`-- Anomaly scan runs automatically on table open
-- No configuration needed

-- Health Score: 65 / 100
-- ✗ CRITICAL  id    — 3 duplicate values
--              rows: 34, 35, 102
-- ✗ WARNING   email — 2 invalid formats
--              rows: 12, 47
-- ✗ WARNING   price — 1 negative value
--              row 89: -250.00
-- ✓ INFO      phone — 73% empty column

-- Click any issue → highlights affected rows`, lang:"SQL" },
    { icon:GitBranch, title:"Data Lineage", desc:"Every SQL query and Python execution is recorded. A git-like branch graph visualises table flow — which tables fed each mutation, when, and with what code.", code:`-- Every mutation is tracked automatically
INSERT INTO west_sales
SELECT * FROM sales_2024
WHERE region = 'West';
-- ✓ Lineage recorded: sales_2024 → west_sales

UPDATE west_sales
SET price = price * 1.1
WHERE category = 'Premium';
-- ✓ Commit a3f9c21b logged
-- ✓ Snapshot saved — rollback available

-- Data Lineage: git-like branch graph
-- Tables are lanes · Queries are commits`, lang:"SQL" },
    { icon:History, title:"Versioning & Rollback", desc:"Every INSERT, UPDATE, DELETE, or CREATE TABLE auto-snapshots the affected table. One click rolls back any DuckDB, MySQL, or PostgreSQL table to any prior commit.", code:`-- Accidentally deleted production data?
DELETE FROM customers;  -- no WHERE clause
-- ✓ Commit b7e2a14f: snapshot taken before

-- In Data Lineage → Versions:
-- b7e2a14f  DELETE  customers
--   10 → 0 rows   (−10)
--   [Rollback to this version]

-- Rollback restores DuckDB + syncs to MySQL/PG
-- Rollback itself is a new commit — revertible
-- Works across app restarts`, lang:"SQL" },
    { icon:BarChart2, title:"BI Dashboard", desc:"No-code charting and stats on any table. Column type inference, distribution stats, and draggable panels — bar, line, area, pie, scatter — all from loaded data.", code:`# Open any table → click "Open in Insight"
# Auto-statistics per column:
#   Numeric: count, nulls, min/max/mean/median
#   Text:    count, nulls, unique, top-10 values

# Build charts — zero code:
#   Type:    Bar | Line | Area | Pie | Scatter
#   X Axis:  region
#   Y Axis:  revenue   (aggregation: SUM)
#   Group:   quarter

# Panels are draggable and persistent
# State survives tab switching`, lang:"Python" },
  ],
  workflow:[
    { n:"01", title:"Open a project", desc:"Create or restore a workspace. Tabs, connections, queries, and DuckDB path are saved and restored automatically." },
    { n:"02", title:"Load your data", desc:"Open CSV/Excel files or connect MySQL and PostgreSQL. Tables register instantly — no data loaded until first query." },
    { n:"03", title:"Query with SQL", desc:"Write SQL against any table — files and databases share the same namespace. Cross-source JOINs work transparently." },
    { n:"04", title:"Analyze in Python", desc:"Every table is already a DataFrame. Transform, visualize, model. Mix duck.sql() and pandas. Plotly renders inline." },
    { n:"05", title:"Review anomalies", desc:"Anomaly scans run automatically. Click any issue to highlight affected rows. Health score at a glance." },
    { n:"06", title:"Track & rollback", desc:"Every mutation commits to lineage and version history. Roll back any table to any prior state in one click." },
  ],
  compare:[
    { task:"Read a CSV file", before:"pd.read_csv('C:/full/path/to/file.csv')", after:"df = sales_2024.copy()  # auto-injected" },
    { task:"Connect to PostgreSQL", before:"sqlalchemy engine + 6 lines of boilerplate", after:"Connect in UI → tables available instantly" },
    { task:"SQL result in Python", before:"Export CSV → re-import in Jupyter", after:"duck.sql('SELECT ...').df()" },
    { task:"Detect data anomalies", before:"Write custom validation scripts", after:"Automatic — runs on every table open" },
    { task:"Undo a bad DELETE", before:"Restore from backup (if one exists)", after:"Lineage → Versions → Rollback" },
    { task:"Audit who changed what", before:"Check git blame / DB logs manually", after:"Data Lineage — full timeline with SQL" },
  ],
  specs:[
    { label:"Platform", value:"macOS · Windows · Linux" },
    { label:"Architecture", value:"Electron + React + DuckDB" },
    { label:"Python runtime", value:"Embedded 3.10 — no install" },
    { label:"SQL engine", value:"DuckDB (Singularity Engine)" },
    { label:"Databases", value:"MySQL, PostgreSQL" },
    { label:"File formats", value:"CSV, XLS, XLSX" },
    { label:"Versioning", value:"Auto-snapshot on every mutation" },
    { label:"Version", value:"0.3.0 Beta" },
  ],
};

const STACK = ["React","TypeScript","DuckDB","Electron","Python","Node.js","Java","PostgreSQL","MySQL","Tailwind","Vite","Spring Boot","C# .NET","Pandas","Figma","Git","Linux"];

/* ══════════════════════════════════════════════════════
   MAGNETIC BUTTON WRAPPER
══════════════════════════════════════════════════════ */
function Magnetic({ children, strength = 0.28, className = "" }) {
  const lite = useLite();
  const ref = useRef(null);
  const x = useMotionValue(0), y = useMotionValue(0);
  const sx = useSpring(x, { stiffness: 140, damping: 14, mass: 0.1 });
  const sy = useSpring(y, { stiffness: 140, damping: 14, mass: 0.1 });
  if (lite) return <span className={`inline-block ${className}`}>{children}</span>;
  return (
    <motion.div ref={ref} className={`inline-block ${className}`} style={{ x: sx, y: sy }}
      onMouseMove={e => {
        const r = ref.current.getBoundingClientRect();
        x.set((e.clientX - r.left - r.width / 2) * strength);
        y.set((e.clientY - r.top - r.height / 2) * strength);
      }}
      onMouseLeave={() => { x.set(0); y.set(0); }}>
      {children}
    </motion.div>
  );
}

/* ══════════════════════════════════════════════════════
   3D TILT CARD
══════════════════════════════════════════════════════ */
function TiltCard({ children, className = "" }) {
  const lite = useLite();
  const ref = useRef(null);
  const mx = useMotionValue(0), my = useMotionValue(0);
  const rotX = useTransform(my, [-120, 120], [9, -9]);
  const rotY = useTransform(mx, [-120, 120], [-9, 9]);
  const srX = useSpring(rotX, { stiffness: 220, damping: 28 });
  const srY = useSpring(rotY, { stiffness: 220, damping: 28 });
  if (lite) return <div className={className}>{children}</div>;
  return (
    <motion.div ref={ref} className={className}
      style={{ rotateX: srX, rotateY: srY, transformPerspective: 1100, transformStyle: "preserve-3d" }}
      onMouseMove={e => {
        const r = ref.current.getBoundingClientRect();
        mx.set(e.clientX - r.left - r.width / 2);
        my.set(e.clientY - r.top - r.height / 2);
      }}
      onMouseLeave={() => { mx.set(0); my.set(0); }}>
      {children}
    </motion.div>
  );
}

/* ══════════════════════════════════════════════════════
   FLOATING PARTICLES
══════════════════════════════════════════════════════ */
function Particles({ count = 28 }) {
  const lite = useLite();
  const [pts] = useState(() =>
    Array.from({ length: count }, (_, i) => {
      const s = ((i * 2654435761) & 0xffffffff) >>> 0;
      return {
        id: i,
        x: ((s & 0xfff) / 0xfff) * 100,
        y: (((s >> 12) & 0xfff) / 0xfff) * 100,
        sz: (((s >> 24) & 0xf) / 0xf) * 1.8 + 0.5,
        op: (((s >> 8) & 0xff) / 0xff) * 0.18 + 0.04,
        dur: ((((s >> 16) & 0xff) / 0xff) * 12 + 9).toFixed(1),
        del: (-(((s >> 20) & 0xff) / 0xff) * 20).toFixed(1),
      };
    })
  );
  if (lite) return null;
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
      {pts.map(p => (
        <div key={p.id} className="absolute rounded-full bg-white"
          style={{ left:`${p.x}%`, top:`${p.y}%`, width:`${p.sz}px`, height:`${p.sz}px`,
            opacity: p.op, animation:`particle-float ${p.dur}s ${p.del}s ease-in-out infinite` }} />
      ))}
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   MOUSE GLOW
══════════════════════════════════════════════════════ */
function MouseGlow() {
  const lite = useLite();
  const x = useMotionValue(-400), y = useMotionValue(-400);
  const sx = useSpring(x, { stiffness: 70, damping: 18 });
  const sy = useSpring(y, { stiffness: 70, damping: 18 });
  useEffect(() => {
    if (lite) return;
    const fn = e => { x.set(e.clientX); y.set(e.clientY); };
    window.addEventListener("mousemove", fn, { passive: true });
    return () => window.removeEventListener("mousemove", fn);
  }, [x, y, lite]);
  if (lite) return null;
  return (
    <motion.div className="pointer-events-none fixed z-0"
      style={{ left: sx, top: sy, width: 600, height: 600, x:"-50%", y:"-50%",
        background:"radial-gradient(circle, rgba(89,130,250,0.06) 0%, transparent 65%)", borderRadius:"50%" }} />
  );
}

/* ══════════════════════════════════════════════════════
   REVEAL
══════════════════════════════════════════════════════ */
function Reveal({ children, delay = 0, className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.1 });
  return (
    <motion.div ref={ref} className={className}
      initial={{ opacity: 0, y: 36 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.8, delay, ease: EASE }}>
      {children}
    </motion.div>
  );
}

/* ══════════════════════════════════════════════════════
   PRIMITIVES
══════════════════════════════════════════════════════ */
const Tag = ({ children }) => (
  <span className="rounded-md border border-white/10 bg-white/[0.05] px-2.5 py-1 font-mono text-xs text-white/50">{children}</span>
);

function Pill({ label, color = "blue", pulse = false }) {
  const s = { blue:"border-blue-400/25 bg-blue-400/8 text-blue-300", amber:"border-amber-400/25 bg-amber-400/8 text-amber-300", green:"border-green-400/25 bg-green-400/8 text-green-300", red:"border-red-400/25 bg-red-400/8 text-red-300", slate:"border-white/10 bg-white/[0.04] text-white/35" };
  const d = { blue:"bg-blue-400", amber:"bg-amber-400", green:"bg-green-400", red:"bg-red-400", slate:"bg-white/25" };
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium ${s[color]}`}>
      <span className={`h-1.5 w-1.5 rounded-full ${d[color]} ${pulse?"animate-pulse":""}`} />
      {label}
    </span>
  );
}

const Label = ({ children }) => (
  <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.35em] text-white/25">{children}</p>
);

function Counter({ to, suffix = "" }) {
  const [v, setV] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  useEffect(() => {
    if (!inView) return;
    let n = 0; const step = to / 55;
    const t = setInterval(() => { n += step; if (n >= to) { setV(to); clearInterval(t); } else setV(Math.floor(n)); }, 24);
    return () => clearInterval(t);
  }, [inView, to]);
  return <span ref={ref}>{v}{suffix}</span>;
}

/* ══════════════════════════════════════════════════════
   SYNTAX HIGHLIGHT
══════════════════════════════════════════════════════ */
function hlSQL(r) {
  return r.replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;")
    .replace(/(--[^\n]*)/g,`<span style="color:${CAT.overlay0};font-style:italic">$1</span>`)
    .replace(/\b(SELECT|FROM|WHERE|GROUP\s+BY|ORDER\s+BY|SUM|COUNT|AVG|DESC|ASC|LIMIT|AND|OR|ON|AS|JOIN|LEFT|RIGHT|INNER|INSERT|INTO|UPDATE|SET|DELETE|CREATE|TABLE|VIEW|DROP|DISTINCT|HAVING)\b/g,`<span style="color:${CAT.blue}">$1</span>`)
    .replace(/('(?:[^'\\]|\\.)*')/g,`<span style="color:${CAT.green}">$1</span>`)
    .replace(/\b(\d+\.?\d*)\b/g,`<span style="color:${CAT.peach}">$1</span>`);
}
function hlPY(r) {
  return r.replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;")
    .replace(/(#[^\n]*)/g,`<span style="color:${CAT.overlay0};font-style:italic">$1</span>`)
    .replace(/\b(import|from|def|class|return|if|else|for|in|as|with|True|False|None|and|or|not)\b/g,`<span style="color:${CAT.mauve}">$1</span>`)
    .replace(/\b(pd|np|plt|px|go|duck|df|result|fig)\b/g,`<span style="color:${CAT.blue}">$1</span>`)
    .replace(/('(?:[^'\\]|\\.)*'|"(?:[^"\\]|\\.)*")/g,`<span style="color:${CAT.green}">$1</span>`)
    .replace(/\b(\d+\.?\d*)\b/g,`<span style="color:${CAT.peach}">$1</span>`);
}

function CodeBlock({ code, lang }) {
  const hl = lang === "Python" ? hlPY : hlSQL;
  return (
    <div className="overflow-hidden rounded-2xl border shadow-[0_30px_80px_rgba(0,0,0,0.6)]"
      style={{ background: CAT.mantle, borderColor: CAT.surface0, fontFamily:"'DM Mono',monospace" }}>
      <div className="flex items-center justify-between border-b px-4 py-3"
        style={{ borderColor: CAT.surface0, background: CAT.crust }}>
        <div className="flex gap-1.5">
          <span className="h-3 w-3 rounded-full" style={{ background: CAT.red }} />
          <span className="h-3 w-3 rounded-full" style={{ background: CAT.yellow }} />
          <span className="h-3 w-3 rounded-full" style={{ background: CAT.green }} />
        </div>
        <span className="text-[10px] uppercase tracking-widest" style={{ color: CAT.overlay0 }}>{lang}</span>
        <span className="text-[10px]" style={{ color: CAT.surface1 }}>varan</span>
      </div>
      <div className="overflow-x-auto p-5">
        <table className="w-full border-collapse">
          <tbody>
            {code.split("\n").map((line, i) => (
              <tr key={i}>
                <td className="w-8 select-none pr-4 text-right align-top text-[10px] leading-6" style={{ color: CAT.surface1 }}>{i + 1}</td>
                <td className="whitespace-pre text-[12px] leading-6" style={{ color: CAT.subtext1 }}
                  dangerouslySetInnerHTML={{ __html: hl(line) || "\u00a0" }} />
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   VARAN APP MOCKUP
══════════════════════════════════════════════════════ */
function VaranMockup() {
  const [tab, setTab] = useState(0);
  const [autoCycle, setAutoCycle] = useState(true);
  const wrapRef = useRef(null);
  const tabs = [
    { label:"SQL Console", icon:Database, color:CAT.blue },
    { label:"Python", icon:Terminal, color:CAT.mauve },
    { label:"Anomaly Scan", icon:Shield, color:CAT.red },
    { label:"Data Lineage", icon:GitBranch, color:CAT.teal },
    { label:"Insight BI", icon:BarChart2, color:CAT.peach },
    { label:"Versions", icon:History, color:CAT.yellow },
  ];

  /* Auto-cycle through tabs every 4s — paused on hover or manual click */
  useEffect(() => {
    if (!autoCycle) return;
    const t = setInterval(() => setTab(p => (p + 1) % tabs.length), 4200);
    return () => clearInterval(t);
  }, [autoCycle, tabs.length]);

  const handleTabClick = i => { setTab(i); setAutoCycle(false); };

  const sqlCode = `SELECT
  m.customer_name,
  SUM(p.order_total) AS total,
  COUNT(*) AS orders
FROM mysql_prod__shop__customers m
JOIN pg_analytics__orders p
  ON m.id = p.customer_id
WHERE p.status = 'completed'
GROUP BY m.customer_name
ORDER BY total DESC`;

  const pyCode = `# Tables auto-injected as DataFrames
df = sales_2024.copy()

result = (
  df.groupby('region')
    .agg(revenue=('amount','sum'))
    .sort_values('revenue', ascending=False)
    .reset_index()
)

import plotly.express as px
fig = px.bar(result, x='region', y='revenue')
fig  # renders as interactive chart`;

  return (
    <div ref={wrapRef}
      onMouseEnter={() => setAutoCycle(false)}
      className="w-full overflow-hidden rounded-2xl border shadow-[0_60px_160px_rgba(0,0,0,0.8)]"
      style={{ background: CAT.base, borderColor: CAT.surface0 }}>
      {/* Title bar */}
      <div className="flex items-center gap-3 border-b px-4 py-3" style={{ background: CAT.crust, borderColor: CAT.surface0 }}>
        <div className="flex gap-1.5">
          {[CAT.red, CAT.yellow, CAT.green].map((c,i) => <span key={i} className="h-3 w-3 rounded-full" style={{ background: c }} />)}
        </div>
        <div className="flex flex-1 items-center justify-center gap-2">
          <img src={LOGO} alt="" className="h-4 w-4 rounded" />
          <span className="text-xs font-medium truncate" style={{ color: CAT.subtext1 }}>Varan — sales_analysis.vproj</span>
          <span className="hidden text-[10px] sm:inline" style={{ color: CAT.overlay0 }}>· unsaved</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="hidden items-center gap-1.5 rounded-md border px-2 py-1 lg:flex"
            style={{ borderColor: CAT.surface0, background: CAT.mantle }}>
            <Search className="h-2.5 w-2.5" style={{ color: CAT.overlay0 }} />
            <span className="text-[9px]" style={{ color: CAT.overlay0, fontFamily:"'DM Mono',monospace" }}>Search workspace</span>
            <span className="rounded px-1 text-[8px] font-semibold" style={{ background: CAT.surface0, color: CAT.subtext1 }}>⌘K</span>
          </div>
          <Pill label="v0.3.0" color="amber" />
        </div>
      </div>

      {/* Body */}
      <div className="varan-body flex">
        {/* Sidebar — hidden on mobile */}
        <div className="hidden w-44 shrink-0 border-r p-3 overflow-y-auto md:block" style={{ background: CAT.mantle, borderColor: CAT.surface0 }}>
          <div className="mb-2 flex items-center gap-2" style={{ color: CAT.overlay0 }}>
            <FolderOpen className="h-3 w-3" />
            <span className="text-[9px] uppercase tracking-widest font-semibold">Workspace</span>
          </div>
          {[{ name:"sales_2024.csv", active:true }, { name:"customers.xlsx", active:false }].map(({ name, active }) => (
            <div key={name} className="mb-1 flex cursor-pointer items-center gap-2 rounded-lg px-2.5 py-1.5 text-[11px] transition-colors"
              style={{ background: active ? `${CAT.blue}20` : "transparent", color: active ? CAT.blue : CAT.overlay0, fontFamily:"'DM Mono',monospace" }}>
              <FileText className="h-3 w-3 shrink-0" /><span className="truncate">{name}</span>
            </div>
          ))}
          <div className="mt-4 mb-2 flex items-center gap-2" style={{ color: CAT.overlay0 }}>
            <Database className="h-3 w-3" /><span className="text-[9px] uppercase tracking-widest font-semibold">DuckDB</span>
          </div>
          <div className="mb-1 flex items-center gap-2 rounded-lg px-2.5 py-1.5 text-[11px]"
            style={{ color: CAT.teal, fontFamily:"'DM Mono',monospace" }}>
            <span className="h-1.5 w-1.5 rounded-full flex-shrink-0" style={{ background: CAT.teal }} />
            <span className="truncate">project.duckdb</span>
          </div>
          <div className="mt-4 mb-2 flex items-center gap-2" style={{ color: CAT.overlay0 }}>
            <Server className="h-3 w-3" /><span className="text-[9px] uppercase tracking-widest font-semibold">Sources</span>
          </div>
          {[{ name:"mysql_prod", tables:["customers","orders","products"] }, { name:"pg_analytics", tables:["users","events"] }].map(src => (
            <div key={src.name} className="mb-2">
              <div className="mb-1 flex items-center gap-2 rounded-lg px-2.5 py-1 text-[11px]"
                style={{ color: CAT.subtext1, fontFamily:"'DM Mono',monospace" }}>
                <span className="h-1.5 w-1.5 rounded-full flex-shrink-0" style={{ background: CAT.green }} />
                <span className="truncate">{src.name}</span>
              </div>
              {src.tables.map(t => (
                <div key={t} className="flex items-center gap-2 pl-5 py-0.5 text-[10px]"
                  style={{ color: CAT.overlay0, fontFamily:"'DM Mono',monospace" }}>
                  <Table2 className="h-2.5 w-2.5 shrink-0" /><span>{t}</span>
                </div>
              ))}
            </div>
          ))}
        </div>

        {/* Main */}
        <div className="flex flex-1 flex-col overflow-hidden">
          <div className="relative flex items-center gap-1 overflow-x-auto border-b px-2 py-1.5 scrollbar-hide" style={{ background: CAT.crust, borderColor: CAT.surface0 }}>
            {tabs.map(({ label, icon: Icon, color }, i) => (
              <button key={label} onClick={() => handleTabClick(i)}
                className="relative flex shrink-0 items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[11px] font-medium transition-all"
                style={{
                  background: tab===i ? `${color}18` : "transparent",
                  color: tab===i ? color : CAT.overlay0,
                  boxShadow: tab===i ? `inset 0 0 0 1px ${color}33` : "none",
                }}>
                <Icon className="h-3 w-3" />{label}
                {i===2 && tab!==2 && <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full animate-pulse" style={{ background: CAT.red }} />}
                {i===4 && tab!==4 && <span className="rounded px-1 text-[8px] font-bold" style={{ background:`${CAT.peach}33`, color: CAT.peach }}>NEW</span>}
              </button>
            ))}
            {/* Auto-cycle progress bar (scaleX — no layout) */}
            {autoCycle && (
              <motion.span key={tab} className="absolute bottom-0 left-0 right-0 h-[2px] origin-left"
                style={{ background: tabs[tab].color }}
                initial={{ scaleX: 0 }} animate={{ scaleX: 1 }} transition={{ duration: 4.2, ease: "linear" }} />
            )}
          </div>

          <AnimatePresence mode="wait">
            <motion.div key={tab} className="flex flex-1 flex-col overflow-hidden"
              initial={{ opacity:0, y:6 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0 }} transition={{ duration:0.18 }}>

              {tab===0 && (<>
                <div className="flex-1 overflow-auto p-3" style={{ fontFamily:"'DM Mono',monospace" }}>
                  <table className="w-full border-collapse"><tbody>
                    {sqlCode.split("\n").map((line,i) => (
                      <tr key={i}>
                        <td className="w-7 select-none pr-3 text-right text-[10px] leading-5 align-top" style={{ color: CAT.surface1 }}>{i+1}</td>
                        <td className="whitespace-pre text-[11px] leading-5" style={{ color: CAT.subtext1 }}
                          dangerouslySetInnerHTML={{ __html: hlSQL(line)||"\u00a0" }} />
                      </tr>
                    ))}
                  </tbody></table>
                </div>
                <div className="border-t" style={{ borderColor: CAT.surface0, background: CAT.mantle }}>
                  <div className="flex items-center justify-between px-3 py-1.5">
                    <span className="text-[10px]" style={{ color: CAT.overlay0 }}>Result — 5 rows · 3 cols</span>
                    <span className="flex items-center gap-1 text-[10px]" style={{ color: CAT.green }}><CheckCircle2 className="h-3 w-3" /> 24ms</span>
                  </div>
                  <div className="overflow-x-auto px-3 pb-2.5">
                    <table className="w-full text-[10px]" style={{ fontFamily:"'DM Mono',monospace" }}>
                      <thead><tr className="border-b" style={{ borderColor: CAT.surface0 }}>
                        {["customer_name","total","orders"].map(h => <th key={h} className="py-1 pr-8 text-left font-medium" style={{ color: CAT.overlay0 }}>{h}</th>)}
                      </tr></thead>
                      <tbody>
                        {[["Acme Corp","$284,739","120"],["Globex Ltd","$210,384","98"],["Initech","$192,410","84"],["Umbrella Co","$84,329","39"]].map(([n,t,o]) => (
                          <tr key={n} className="border-b" style={{ borderColor:`${CAT.surface0}55` }}>
                            <td className="py-1 pr-8" style={{ color: CAT.text }}>{n}</td>
                            <td className="py-1 pr-8" style={{ color: CAT.blue }}>{t}</td>
                            <td className="py-1 pr-8" style={{ color: CAT.subtext1 }}>{o}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>)}

              {tab===1 && (
                <div className="flex-1 overflow-auto p-3 space-y-3" style={{ fontFamily:"'DM Mono',monospace" }}>
                  <div className="rounded-xl border overflow-hidden" style={{ borderColor: CAT.surface0 }}>
                    <div className="flex items-center gap-2 border-b px-3 py-1.5" style={{ borderColor: CAT.surface0, background: CAT.crust }}>
                      <span className="text-[9px] font-semibold rounded px-1.5 py-0.5" style={{ background:`${CAT.mauve}33`, color: CAT.mauve }}>In [1]</span>
                      <span className="text-[9px]" style={{ color: CAT.overlay0 }}>Python · Varan Runtime 3.10</span>
                    </div>
                    <div className="p-3">
                      <table className="w-full border-collapse"><tbody>
                        {pyCode.split("\n").map((line,i) => (
                          <tr key={i}>
                            <td className="w-7 select-none pr-3 text-right text-[10px] leading-5 align-top" style={{ color: CAT.surface1 }}>{i+1}</td>
                            <td className="whitespace-pre text-[11px] leading-5" style={{ color: CAT.subtext1 }}
                              dangerouslySetInnerHTML={{ __html: hlPY(line)||"\u00a0" }} />
                          </tr>
                        ))}
                      </tbody></table>
                    </div>
                    <div className="border-t px-3 py-2" style={{ borderColor: CAT.surface0, background: CAT.mantle }}>
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="text-[9px] font-semibold rounded px-1.5 py-0.5" style={{ background:`${CAT.green}33`, color: CAT.green }}>Out [1]</span>
                        <span className="text-[9px]" style={{ color: CAT.overlay0 }}>DataFrame · 4 rows × 2 cols</span>
                      </div>
                      <table className="text-[10px]"><thead><tr>
                        {["region","revenue"].map(h => <th key={h} className="pr-8 text-left pb-1 font-medium" style={{ color: CAT.overlay0 }}>{h}</th>)}
                      </tr></thead><tbody>
                        {[["EMEA","2,847,391"],["APAC","2,103,847"],["AMER","1,924,103"],["LATAM","843,291"]].map(([r,v]) => (
                          <tr key={r}><td className="pr-8" style={{ color: CAT.text }}>{r}</td><td style={{ color: CAT.blue }}>{v}</td></tr>
                        ))}
                      </tbody></table>
                    </div>
                  </div>
                </div>
              )}

              {tab===2 && (
                <div className="flex-1 overflow-auto p-3 space-y-2">
                  <div className="flex items-center justify-between rounded-xl border px-4 py-3"
                    style={{ borderColor:`${CAT.red}55`, background:`${CAT.red}11` }}>
                    <div className="flex items-center gap-2.5">
                      <AlertTriangle className="h-4 w-4" style={{ color: CAT.red }} />
                      <span className="text-sm font-semibold" style={{ color: CAT.red }}>4 anomalies detected</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-[11px]" style={{ color: CAT.overlay0 }}>Health:</span>
                      <span className="font-mono text-sm font-bold" style={{ color: CAT.red }}>65 / 100</span>
                    </div>
                  </div>
                  {[
                    { type:"CRITICAL", col:"id", msg:"3 duplicate values at rows 34, 35, 102", color: CAT.red },
                    { type:"WARNING",  col:"email", msg:"2 invalid formats (rows 12, 47)", color: CAT.yellow },
                    { type:"WARNING",  col:"price", msg:"1 negative value: row 89 (−250.00)", color: CAT.yellow },
                    { type:"INFO",     col:"phone", msg:"Column is 73% empty", color: CAT.sapphire },
                  ].map((issue, i) => (
                    <div key={i} className="flex items-start gap-3 rounded-xl border px-3 py-2.5 cursor-pointer transition-all hover:brightness-110"
                      style={{ borderColor:`${issue.color}33`, background:`${issue.color}0d` }}>
                      <span className="mt-0.5 rounded px-1.5 py-0.5 font-mono text-[9px] font-bold"
                        style={{ background:`${issue.color}22`, color: issue.color }}>{issue.type}</span>
                      <div>
                        <span className="font-mono text-[11px] font-semibold" style={{ color: issue.color }}>{issue.col}</span>
                        <span className="mx-1.5 text-[10px]" style={{ color: CAT.overlay0 }}>—</span>
                        <span className="text-[11px]" style={{ color: CAT.subtext1 }}>{issue.msg}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {tab===4 && (
                <div className="flex-1 overflow-auto p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <div className="flex items-center gap-2 text-[10px]" style={{ color: CAT.overlay0 }}>
                      <span style={{ color: CAT.peach }}>sales_2024</span><span>·</span><span>4 panels · auto-saved</span>
                    </div>
                    <div className="flex gap-1">
                      {["Bar","Line","Pie","Scatter"].map(t => (
                        <span key={t} className="rounded px-1.5 py-0.5 text-[9px] font-medium"
                          style={{ background:`${CAT.peach}15`, color: CAT.peach, fontFamily:"'DM Mono',monospace" }}>{t}</span>
                      ))}
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {/* Bar chart */}
                    <div className="rounded-xl border p-2.5" style={{ borderColor: CAT.surface0, background: CAT.mantle }}>
                      <div className="mb-1 flex items-center justify-between">
                        <span className="text-[10px] font-semibold" style={{ color: CAT.text }}>Revenue · by region</span>
                        <Maximize2 className="h-2.5 w-2.5" style={{ color: CAT.overlay0 }} />
                      </div>
                      <div className="flex h-16 items-end gap-1.5 pt-1">
                        {[85, 62, 73, 40, 91, 55].map((h, i) => (
                          <div key={i} className="flex-1 rounded-sm" style={{ height:`${h}%`, background: `linear-gradient(180deg, ${CAT.peach} 0%, ${CAT.red} 100%)`, opacity: 0.85 }} />
                        ))}
                      </div>
                      <div className="mt-1 flex justify-between text-[8px]" style={{ color: CAT.overlay0, fontFamily:"'DM Mono',monospace" }}>
                        <span>EMEA</span><span>APAC</span><span>AMER</span><span>LATAM</span><span>MEA</span><span>SEA</span>
                      </div>
                    </div>
                    {/* Line chart */}
                    <div className="rounded-xl border p-2.5" style={{ borderColor: CAT.surface0, background: CAT.mantle }}>
                      <div className="mb-1 flex items-center justify-between">
                        <span className="text-[10px] font-semibold" style={{ color: CAT.text }}>Orders · monthly</span>
                        <TrendingUp className="h-2.5 w-2.5" style={{ color: CAT.green }} />
                      </div>
                      <svg viewBox="0 0 100 40" className="h-16 w-full">
                        <defs>
                          <linearGradient id="mk-grad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={CAT.green} stopOpacity="0.5" />
                            <stop offset="100%" stopColor={CAT.green} stopOpacity="0" />
                          </linearGradient>
                        </defs>
                        <path d="M0,30 L15,24 L30,26 L45,18 L60,14 L75,16 L90,8 L100,6 L100,40 L0,40 Z" fill="url(#mk-grad)" />
                        <path d="M0,30 L15,24 L30,26 L45,18 L60,14 L75,16 L90,8 L100,6" stroke={CAT.green} strokeWidth="1.2" fill="none" />
                      </svg>
                      <div className="mt-1 text-[8px]" style={{ color: CAT.overlay0 }}>+24% vs. last quarter</div>
                    </div>
                    {/* Pie chart */}
                    <div className="rounded-xl border p-2.5" style={{ borderColor: CAT.surface0, background: CAT.mantle }}>
                      <div className="mb-1 text-[10px] font-semibold" style={{ color: CAT.text }}>Category share</div>
                      <div className="flex items-center gap-2">
                        <svg viewBox="0 0 32 32" className="h-14 w-14 -rotate-90">
                          <circle cx="16" cy="16" r="12" fill="none" stroke={CAT.blue} strokeWidth="7" strokeDasharray="30 75" />
                          <circle cx="16" cy="16" r="12" fill="none" stroke={CAT.mauve} strokeWidth="7" strokeDasharray="22 75" strokeDashoffset="-30" />
                          <circle cx="16" cy="16" r="12" fill="none" stroke={CAT.green} strokeWidth="7" strokeDasharray="14 75" strokeDashoffset="-52" />
                          <circle cx="16" cy="16" r="12" fill="none" stroke={CAT.peach} strokeWidth="7" strokeDasharray="9 75" strokeDashoffset="-66" />
                        </svg>
                        <div className="flex-1 space-y-0.5 text-[8px]" style={{ fontFamily:"'DM Mono',monospace" }}>
                          {[[CAT.blue,"Premium","40%"],[CAT.mauve,"Standard","29%"],[CAT.green,"Basic","19%"],[CAT.peach,"Trial","12%"]].map(([c,n,p]) => (
                            <div key={n} className="flex items-center gap-1.5">
                              <span className="h-1.5 w-1.5 rounded-full" style={{ background: c }} />
                              <span style={{ color: CAT.subtext1 }}>{n}</span>
                              <span className="ml-auto" style={{ color: CAT.overlay0 }}>{p}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                    {/* Stats card */}
                    <div className="rounded-xl border p-2.5" style={{ borderColor: CAT.surface0, background: CAT.mantle }}>
                      <div className="mb-1 text-[10px] font-semibold" style={{ color: CAT.text }}>Column stats · revenue</div>
                      <div className="space-y-1 text-[9px]" style={{ fontFamily:"'DM Mono',monospace" }}>
                        {[["count","10,482"],["nulls","0"],["min","$12.00"],["max","$84,920"],["mean","$1,204.38"],["median","$612.00"]].map(([k,v]) => (
                          <div key={k} className="flex justify-between border-b py-0.5" style={{ borderColor:`${CAT.surface0}55` }}>
                            <span style={{ color: CAT.overlay0 }}>{k}</span>
                            <span style={{ color: CAT.text }}>{v}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {tab===5 && (
                <div className="flex-1 overflow-auto p-3">
                  <div className="mb-2 flex items-center justify-between">
                    <div className="flex items-center gap-3 text-[10px]" style={{ color: CAT.overlay0 }}>
                      <span style={{ color: CAT.yellow }}>west_sales</span><span>·</span><span>12 snapshots</span>
                    </div>
                    <span className="rounded-full px-2 py-0.5 text-[9px] font-semibold"
                      style={{ background: `${CAT.green}22`, color: CAT.green }}>HEAD</span>
                  </div>
                  {[
                    { hash:"a3f9c21b", action:"INSERT", rows:"10 → 57 rows", time:"2m ago", author:"current", head:true, color: CAT.green },
                    { hash:"b7e2a14f", action:"UPDATE", rows:"changed 14 rows", time:"15m ago", author:"current", color: CAT.yellow },
                    { hash:"c1d4e89a", action:"DELETE", rows:"57 → 44 rows", time:"1h ago", author:"current", color: CAT.red },
                    { hash:"d9f3a82c", action:"CREATE", rows:"0 → 10 rows", time:"2h ago", author:"origin", color: CAT.blue },
                  ].map(c => (
                    <div key={c.hash} className="mb-2 flex items-center gap-3 rounded-xl border px-3 py-2.5"
                      style={{ borderColor: c.head ? `${CAT.green}44` : CAT.surface0, background: c.head ? `${CAT.green}08` : CAT.mantle }}>
                      <div className="flex h-7 w-7 items-center justify-center rounded-lg" style={{ background:`${c.color}22` }}>
                        <GitCommit className="h-3 w-3" style={{ color: c.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-[10px]" style={{ color: CAT.overlay0 }}>{c.hash}</span>
                          <span className="rounded px-1.5 py-0.5 text-[8px] font-bold" style={{ background:`${c.color}22`, color: c.color }}>{c.action}</span>
                          <span className="text-[10px]" style={{ color: CAT.text }}>{c.rows}</span>
                        </div>
                        <div className="flex items-center gap-2 text-[9px] mt-0.5" style={{ color: CAT.overlay0 }}>
                          <Clock className="h-2.5 w-2.5" /><span>{c.time}</span><span>·</span><span>{c.author}</span>
                        </div>
                      </div>
                      {!c.head && (
                        <button className="rounded-md px-2 py-1 text-[9px] font-semibold transition-all"
                          style={{ background:`${CAT.yellow}18`, color: CAT.yellow, fontFamily:"'DM Mono',monospace" }}>
                          Rollback ⎌
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {tab===3 && (
                <div className="flex-1 overflow-auto p-3">
                  <div className="mb-2 flex items-center gap-3 text-[10px]" style={{ color: CAT.overlay0 }}>
                    <span>6 commits</span><span>·</span><span>Last: 2 min ago</span>
                  </div>
                  {[
                    { hash:"a3f9c21b", action:"INSERT", table:"west_sales", engine:"DuckDB", time:"2m ago",  rows:"+47",   color: CAT.green },
                    { hash:"b7e2a14f", action:"DELETE", table:"customers", engine:"MySQL",  time:"15m ago", rows:"−3",    color: CAT.red },
                    { hash:"c1d4e89a", action:"UPDATE", table:"products",  engine:"DuckDB", time:"1h ago",  rows:"~120",  color: CAT.yellow },
                    { hash:"d9f3a82c", action:"CREATE", table:"summary",   engine:"DuckDB", time:"2h ago",  rows:"+1.2k", color: CAT.blue },
                  ].map(c => (
                    <div key={c.hash} className="mb-2 flex items-center gap-3 rounded-xl border px-3 py-2.5 hover:brightness-110 transition-all"
                      style={{ borderColor: CAT.surface0, background: CAT.mantle }}>
                      <div className="flex h-8 w-8 items-center justify-center rounded-lg flex-shrink-0" style={{ background:`${c.color}22` }}>
                        <GitCommit className="h-3.5 w-3.5" style={{ color: c.color }} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-0.5">
                          <span className="font-mono text-[10px]" style={{ color: CAT.overlay0 }}>{c.hash}</span>
                          <span className="rounded px-1.5 py-0.5 text-[9px] font-semibold" style={{ background:`${c.color}22`, color: c.color }}>{c.action}</span>
                          <span className="font-mono text-[11px]" style={{ color: CAT.text }}>{c.table}</span>
                        </div>
                        <div className="flex items-center gap-3 text-[10px]" style={{ color: CAT.overlay0 }}>
                          <span>{c.engine}</span><span style={{ color: c.color }}>{c.rows} rows</span><span>{c.time}</span>
                        </div>
                      </div>
                      <RefreshCw className="h-3.5 w-3.5 shrink-0" style={{ color: CAT.overlay0 }} />
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* Status bar */}
      <div className="flex items-center justify-between border-t px-4 py-1.5" style={{ borderColor: CAT.surface0, background: CAT.crust }}>
        <div className="flex items-center gap-4 text-[9px]" style={{ fontFamily:"'DM Mono',monospace", color: CAT.overlay0 }}>
          <span className="flex items-center gap-1"><span className="h-1.5 w-1.5 rounded-full" style={{ background: CAT.green }} /> Connected</span>
          <span>2 sources</span>
          <span style={{ color: CAT.blue }}>DuckDB Singularity Engine</span>
        </div>
        <div className="text-[9px]" style={{ fontFamily:"'DM Mono',monospace", color: CAT.overlay0 }}>Python 3.10 · Varan Runtime</div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   TICKER
══════════════════════════════════════════════════════ */
function Ticker() {
  const doubled = [...STACK, ...STACK];
  return (
    <div className="overflow-hidden border-y border-white/[0.05] py-4 bg-white/[0.012]">
      <div className="ticker-track flex gap-10 whitespace-nowrap">
        {doubled.map((item, i) => (
          <span key={i} className="shrink-0 inline-flex items-center gap-10">
            <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-white/20">{item}</span>
            <span className="text-white/8 text-sm">·</span>
          </span>
        ))}
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   NAV
══════════════════════════════════════════════════════ */
function Nav() {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);
  return (
    <motion.header initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}
      className={`fixed top-0 z-50 w-full transition-all duration-500 ${scrolled ? "bg-black/80 backdrop-blur-2xl border-b border-white/[0.06]" : ""}`}>
      <div className="mx-auto flex max-w-6xl items-center justify-between px-6 py-5 md:px-10">
        <a href="#top" className="group flex items-center gap-2.5">
          <div className="flex h-8 w-8 items-center justify-center rounded-xl border border-white/10 bg-white/[0.04] transition group-hover:border-blue-400/30 group-hover:bg-blue-500/10">
            <Layers3 className="h-4 w-4 text-white/40 transition group-hover:text-blue-300" />
          </div>
          <span className="text-sm font-medium text-white/40 transition group-hover:text-white/80">{P.name.split(" ")[0]}</span>
        </a>
        <nav className="hidden items-center gap-8 text-sm text-white/35 md:flex">
          {["About","Varan","Projects","Contact"].map(s => (
            <a key={s} href={`#${s.toLowerCase()}`}
              className="relative transition hover:text-white after:absolute after:-bottom-0.5 after:left-0 after:h-px after:w-0 after:bg-white/50 after:transition-all hover:after:w-full">
              {s}
            </a>
          ))}
        </nav>
        <Magnetic>
          <a href={`mailto:${P.email}`}
            className="hidden items-center gap-1.5 rounded-full border border-white/[0.1] bg-white/[0.04] px-4 py-2 text-xs font-medium text-white/45 transition hover:bg-white/[0.09] hover:text-white md:flex">
            <Mail className="h-3 w-3" /> Email me
          </a>
        </Magnetic>
      </div>
    </motion.header>
  );
}

/* ══════════════════════════════════════════════════════
   HERO — Apple-level
══════════════════════════════════════════════════════ */
function Hero() {
  const [ready, setReady] = useState(false);
  const heroRef = useRef(null);
  useEffect(() => { const t = setTimeout(() => setReady(true), 80); return () => clearTimeout(t); }, []);

  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const titleY = useTransform(scrollYProgress, [0, 1], [0, -160]);
  const titleScale = useTransform(scrollYProgress, [0, 1], [1, 0.86]);
  const heroFade = useTransform(scrollYProgress, [0, 0.9], [1, 0]);
  const gridY = useTransform(scrollYProgress, [0, 1], [0, 160]);

  return (
    <section ref={heroRef} className="hero-section relative flex min-h-screen flex-col items-center justify-center overflow-hidden px-6 text-center">
      {/* layered background */}
      <div className="absolute inset-0 bg-black" />
      <div className="mesh-gradient mesh-gradient-hero" />
      <div className="hero-orb hero-orb-1" />
      <div className="hero-orb hero-orb-2" />
      <div className="hero-orb hero-orb-3" />
      <motion.div className="hero-grid" style={{ y: gridY }} />
      <div className="spotlight-top" />
      <Particles count={30} />

      <motion.div className="relative z-10 max-w-5xl w-full" style={{ y: titleY, scale: titleScale, opacity: heroFade }}>
        <AnimatePresence>
          {ready && (<>
            {/* Avatar */}
            <motion.div initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, ease: EASE }}
              className="mb-10 flex flex-col items-center gap-5">
              <div className="relative">
                <div className="avatar-ring" />
                <div className="h-32 w-32 overflow-hidden rounded-full border border-white/10 relative z-10 shadow-[0_0_60px_rgba(89,130,250,0.35)]">
                  <img src={P.avatar} alt={P.name} className="h-full w-full object-cover"
                    onError={e => { e.target.style.display="none"; }} />
                </div>
                <span className="absolute bottom-0.5 right-0.5 z-10 h-4 w-4 rounded-full border-2 border-black bg-green-400 shadow-[0_0_10px_rgba(166,227,161,0.7)]" />
              </div>
              <div className="flex flex-wrap items-center justify-center gap-2">
                <Pill label={P.location} color="blue" />
                <Pill label="Open to opportunities" color="green" pulse />
              </div>
            </motion.div>

            {/* Name — character-by-character cinematic reveal */}
            <div className="mb-6">
              {["Murad", "Aghamirzayev"].map((word, wi) => (
                <div key={word} className="overflow-hidden leading-[0.95] text-center">
                  {word.split("").map((ch, ci) => (
                    <motion.span
                      key={ci}
                      initial={{ y: "115%", rotate: 7, opacity: 0 }}
                      animate={{ y: 0, rotate: 0, opacity: 1 }}
                      transition={{
                        duration: 1.05,
                        delay: 0.1 + wi * 0.14 + ci * 0.035,
                        ease: EASE,
                      }}
                      className={`inline-block tracking-[-0.04em] font-semibold hero-name ${wi === 0 ? "shimmer-text" : "text-white"}`}>
                      {ch}
                    </motion.span>
                  ))}
                </div>
              ))}
            </div>

            <motion.div initial={{ scaleX: 0, opacity: 0 }} animate={{ scaleX: 1, opacity: 1 }}
              transition={{ duration: 0.9, delay: 0.42, ease: EASE }} style={{ originX: 0.5 }}
              className="mx-auto mb-7 h-px w-20 bg-gradient-to-r from-transparent via-white/30 to-transparent" />

            <motion.p initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.5 }}
              className="mb-2 text-lg font-light tracking-wide text-white/40 md:text-xl">
              {P.role} <span className="text-white/15 px-2">·</span> {P.sub}
            </motion.p>
            <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.58 }}
              className="mx-auto mb-11 max-w-lg text-base leading-7 text-white/35 md:text-lg">
              {P.bio}
            </motion.p>

            {/* CTAs — magnetic */}
            <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.66 }}
              className="flex flex-wrap items-center justify-center gap-3">
              <Magnetic strength={0.22}>
                <a href={`mailto:${P.email}`}
                  className="group inline-flex items-center gap-2 rounded-full bg-white px-7 py-3.5 text-sm font-semibold text-black shadow-[0_0_50px_rgba(255,255,255,0.18)] transition-all hover:shadow-[0_0_80px_rgba(255,255,255,0.3)] hover:scale-105">
                  <Mail className="h-4 w-4" /> Get in touch
                  <ArrowUpRight className="h-3.5 w-3.5 opacity-25 transition group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
              </Magnetic>
              <Magnetic strength={0.22}>
                <a href={P.github}
                  className="inline-flex items-center gap-2 rounded-full border border-white/[0.12] bg-white/[0.05] px-7 py-3.5 text-sm font-medium text-white/55 backdrop-blur-sm transition hover:bg-white/[0.1] hover:text-white">
                  <Github className="h-4 w-4" /> GitHub
                </a>
              </Magnetic>
              <Magnetic strength={0.22}>
                <a href={P.linkedin}
                  className="inline-flex items-center gap-2 rounded-full border border-white/[0.12] bg-white/[0.05] px-7 py-3.5 text-sm font-medium text-white/55 backdrop-blur-sm transition hover:bg-white/[0.1] hover:text-white">
                  <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
                  LinkedIn
                </a>
              </Magnetic>
              <Magnetic strength={0.22}>
                <a href="#varan"
                  className="inline-flex items-center gap-2 rounded-full border border-blue-500/30 bg-blue-500/10 px-7 py-3.5 text-sm font-medium text-blue-300 transition hover:bg-blue-500/20 hover:border-blue-400/50">
                  See Varan <ChevronRight className="h-3.5 w-3.5" />
                </a>
              </Magnetic>
            </motion.div>
          </>)}
        </AnimatePresence>
      </motion.div>

      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2, duration: 1 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 text-white/15">
        <span className="text-[10px] uppercase tracking-[0.3em] mb-1">Scroll</span>
        <div className="scroll-caret"><ChevronDown className="h-4 w-4" /></div>
      </motion.div>
    </section>
  );
}

/* ══════════════════════════════════════════════════════
   SCROLL PROGRESS BAR
══════════════════════════════════════════════════════ */
function ScrollProgress() {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 30, mass: 0.2 });
  return (
    <motion.div className="fixed top-0 left-0 right-0 z-[60] h-[2px] origin-left"
      style={{ scaleX, background: "linear-gradient(90deg, #60a5fa, #818cf8, #38bdf8)" }} />
  );
}


/* ══════════════════════════════════════════════════════
   ABOUT — Apple big-stat style
══════════════════════════════════════════════════════ */
function About() {
  const stats = [
    { n:3, s:"+", label:"Years of experience", desc:"Building production software across web, desktop, data, and systems" },
    { n:30, s:"+", label:"Projects shipped", desc:"From mobile apps to desktop tools to embedded systems" },
    { n:4, s:"+", label:"Core technology stacks", desc:"Full-stack, systems, data engineering, and desktop development" },
    { n:1, s:"", label:"Flagship product", desc:"Varan — a desktop data workspace in active beta" },
  ];

  return (
    <section id="about" className="relative bg-black px-6 pt-20 pb-16 md:px-10 md:pt-32 md:pb-24 lg:px-20">
      <div className="mx-auto max-w-6xl">
        <Reveal>
          <Label>About</Label>
          <h2 className="mb-4 max-w-3xl text-[clamp(2.4rem,5.5vw,4.5rem)] font-semibold leading-[1.08] tracking-tight text-white">
            Engineering depth<br />meets product thinking.
          </h2>
          <p className="mb-20 max-w-lg text-lg leading-7 text-white/35">
            I care about the full stack — pixels to pipelines, interfaces to infrastructure. I ship things that feel good to use.
          </p>
        </Reveal>

        {/* Apple-style big stat rows */}
        <div className="mb-20 md:mb-28">
          {stats.map((stat, i) => (
            <Reveal key={stat.label} delay={i * 0.07}>
              <div className="group flex flex-col gap-3 border-t border-white/[0.06] py-6 last:border-b cursor-default hover:border-white/[0.12] transition-colors md:flex-row md:items-center md:gap-8 md:py-8">
                <div className="flex items-center gap-5 md:contents">
                  <span className="w-6 shrink-0 font-mono text-xs text-white/15 group-hover:text-white/25 transition-colors md:w-8 md:text-sm">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="stat-number text-[clamp(2.8rem,11vw,7rem)] font-bold tracking-tight leading-none shimmer-text md:w-48 md:shrink-0">
                    <Counter to={stat.n} suffix={stat.s} />
                  </div>
                </div>
                <div className="pl-11 md:pl-0">
                  <div className="text-base md:text-xl font-semibold text-white mb-1">{stat.label}</div>
                  <div className="text-white/35 text-sm leading-6">{stat.desc}</div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>

        {/* Experience + Skills */}
        <div className="grid gap-16 lg:grid-cols-2">
          <Reveal>
            <div className="mb-6 flex items-center gap-2.5 text-white/25">
              <Briefcase className="h-3.5 w-3.5" />
              <span className="text-[10px] uppercase tracking-[0.28em]">Experience</span>
            </div>
            <div className="space-y-7">
              {P.experience.map((item, i) => (
                <div key={i} className="group relative border-l border-white/[0.07] pl-6 transition-colors hover:border-white/20">
                  <span className="absolute -left-[3px] top-2 h-1.5 w-1.5 rounded-full bg-white/20 transition-colors group-hover:bg-blue-400" />
                  <div className="flex flex-wrap items-start justify-between gap-2 mb-1">
                    <div>
                      <h4 className="font-semibold text-white/90">{item.role}</h4>
                      <p className="text-sm text-blue-400/60 mt-0.5">{item.company}</p>
                    </div>
                    <span className="rounded-full border border-white/[0.07] px-2.5 py-0.5 text-[11px] text-white/22">{item.period}</span>
                  </div>
                  <p className="text-sm leading-6 text-white/35">{item.desc}</p>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="mb-6 flex items-center gap-2.5 text-white/25">
              <GraduationCap className="h-3.5 w-3.5" />
              <span className="text-[10px] uppercase tracking-[0.28em]">Education</span>
            </div>
            <div className="group relative border-l border-white/[0.07] pl-6 mb-12 hover:border-white/20 transition-colors">
              <span className="absolute -left-[3px] top-2 h-1.5 w-1.5 rounded-full bg-white/20" />
              <h4 className="font-semibold text-white/90">{P.education.school}</h4>
              <p className="text-sm text-blue-400/60">{P.education.degree}</p>
              <span className="text-xs text-white/22">{P.education.period}</span>
            </div>

            <div className="mb-6 flex items-center gap-2.5 text-white/25">
              <Code2 className="h-3.5 w-3.5" />
              <span className="text-[10px] uppercase tracking-[0.28em]">Skills</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {Object.entries(P.skills).map(([cat, items]) => (
                <TiltCard key={cat}>
                  <div className="rounded-xl border border-white/[0.06] bg-white/[0.025] p-4 hover:border-white/10 transition-colors">
                    <p className="mb-3 font-mono text-[10px] uppercase tracking-wider text-white/20">{cat}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {items.slice(0, 4).map(s => <Tag key={s}>{s}</Tag>)}
                    </div>
                  </div>
                </TiltCard>
              ))}
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════════════════════════════════
   WHAT'S NEW — Apple "New features" ribbon
══════════════════════════════════════════════════════ */
function WhatsNew() {
  return (
    <div className="relative px-6 py-28 md:px-10 lg:px-20 border-t border-white/[0.04]">
      <div className="absolute inset-0 -z-0 opacity-60"
        style={{ background: "radial-gradient(ellipse 80% 50% at 50% 0%, rgba(59,130,246,0.1), transparent 60%)" }} />
      <div className="relative mx-auto max-w-6xl">
        <div className="mb-14 flex items-end justify-between gap-8 flex-wrap">
          <div>
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-blue-500/30 bg-blue-500/8 px-3 py-1 text-[10px] font-mono uppercase tracking-widest text-blue-300">
              <span className="h-1.5 w-1.5 rounded-full bg-blue-400 animate-pulse" />
              New in v{VARAN.version}
            </div>
            <h3 className="text-[clamp(2rem,5vw,4rem)] font-semibold tracking-[-0.03em] text-white leading-[1.05]">
              Six features.<br />
              <span className="shimmer-text">One major release.</span>
            </h3>
          </div>
          <p className="max-w-sm text-base leading-7 text-white/40">
            Each release of Varan gets sharper. v0.3.0 brings the Insight BI Dashboard, Time-Travel Rollback, and a 3× faster DuckDB kernel.
          </p>
        </div>
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {VARAN.whatsNew.map(({ tag, icon: Icon, title, desc }, i) => (
            <Reveal key={title} delay={i * 0.05}>
              <TiltCard className="h-full">
                <div className="group relative h-full overflow-hidden rounded-2xl border border-white/[0.07] bg-gradient-to-br from-white/[0.035] to-white/[0.01] p-6 transition-all hover:border-white/15">
                  <div className="absolute -right-10 -top-10 h-32 w-32 rounded-full opacity-0 transition-opacity group-hover:opacity-100"
                    style={{ background: tag==="New" ? "radial-gradient(circle, rgba(59,130,246,0.25), transparent 60%)" : "radial-gradient(circle, rgba(251,191,36,0.2), transparent 60%)" }} />
                  <div className="relative mb-5 flex items-center justify-between">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/10 bg-white/[0.04]">
                      <Icon className="h-4 w-4 text-white/80" />
                    </div>
                    <span className={`rounded-full border px-2.5 py-0.5 text-[9px] font-bold uppercase tracking-wider ${
                      tag==="New"
                        ? "border-blue-400/30 bg-blue-400/10 text-blue-300"
                        : "border-amber-400/30 bg-amber-400/10 text-amber-300"
                    }`}>{tag}</span>
                  </div>
                  <h4 className="relative mb-2 text-lg font-semibold text-white tracking-tight">{title}</h4>
                  <p className="relative text-sm leading-6 text-white/40">{desc}</p>
                </div>
              </TiltCard>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   MANIFESTO — Apple pull-quote section
══════════════════════════════════════════════════════ */
function Manifesto() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [50, -50]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [0.97, 1, 0.97]);

  return (
    <div ref={ref} className="relative overflow-hidden border-y border-white/[0.05] bg-black py-36 px-6 md:px-10 lg:px-20">
      <div className="manifesto-aura" />
      <motion.div style={{ y, scale }} className="relative mx-auto max-w-5xl text-center">
        <Label>{VARAN.manifesto.eyebrow}</Label>
        <h2 className="mt-4 text-[clamp(2.4rem,6vw,5.5rem)] font-semibold leading-[1.05] tracking-[-0.04em] text-white"
          style={{ whiteSpace: "pre-line" }}>
          {VARAN.manifesto.title}
        </h2>
        <p className="mx-auto mt-10 max-w-2xl text-lg leading-8 text-white/45 md:text-xl">
          {VARAN.manifesto.body}
        </p>
        <div className="mx-auto mt-12 h-px w-24 bg-gradient-to-r from-transparent via-white/30 to-transparent" />
        <div className="mt-8 flex flex-wrap items-center justify-center gap-6 text-[11px]" style={{ color: "rgba(255,255,255,0.3)", fontFamily:"'DM Mono',monospace" }}>
          {VARAN.hype.map((h, i) => (
            <span key={i} className="flex items-center gap-2">
              {i > 0 && <span className="h-1 w-1 rounded-full bg-white/10" />}
              "{h}"
            </span>
          ))}
        </div>
      </motion.div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   PROOF — big-number row
══════════════════════════════════════════════════════ */
function ProofRow() {
  return (
    <div className="relative px-6 py-24 md:px-10 lg:px-20 border-t border-white/[0.04]">
      <div className="mx-auto max-w-6xl">
        <Reveal>
          <div className="mb-12 text-center">
            <Label>By the numbers</Label>
            <h3 className="text-[clamp(2rem,4.5vw,3.5rem)] font-semibold tracking-[-0.03em] text-white leading-[1.05]">
              Engineered to feel impossible.
            </h3>
          </div>
        </Reveal>
        <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
          {VARAN.proof.map((p, i) => (
            <Reveal key={p.label} delay={i * 0.07}>
              <div className="text-center">
                <div className="text-[clamp(3rem,7vw,5.5rem)] font-semibold leading-none tracking-[-0.04em] shimmer-text">
                  {p.n}{p.s}
                </div>
                <div className="mt-3 text-sm text-white/40">{p.label}</div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   QUOTES — press-style
══════════════════════════════════════════════════════ */
function Quotes() {
  return (
    <div className="relative px-6 py-24 md:px-10 lg:px-20 border-t border-white/[0.04]">
      <div className="mx-auto max-w-6xl">
        <Reveal>
          <div className="mb-14 text-center">
            <Label>What people say</Label>
            <h3 className="text-[clamp(2rem,4.5vw,3.5rem)] font-semibold tracking-[-0.03em] text-white leading-[1.05]">
              Beta testers don't mince words.
            </h3>
          </div>
        </Reveal>
        <div className="grid gap-5 md:grid-cols-3">
          {VARAN.quotes.map((q, i) => (
            <Reveal key={i} delay={i * 0.08}>
              <TiltCard>
                <div className="relative h-full rounded-2xl border border-white/[0.07] bg-white/[0.02] p-7">
                  <Quote className="absolute right-4 top-4 h-5 w-5 text-white/10" />
                  <p className="text-lg leading-8 text-white/75 font-light tracking-tight">
                    {q.text}
                  </p>
                  <div className="mt-6 flex items-center gap-2 text-xs text-white/30" style={{ fontFamily:"'DM Mono',monospace" }}>
                    <div className="h-px w-6 bg-white/15" />
                    {q.who}
                  </div>
                </div>
              </TiltCard>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   VARAN SECTION
══════════════════════════════════════════════════════ */
function VaranSection() {
  const [activeFeature, setActiveFeature] = useState(0);
  const heroRef = useRef(null);
  const heroInView = useInView(heroRef, { once: true, amount: 0.15 });

  /* Scroll-linked cinematic reveal on the Varan logo+title */
  const { scrollYProgress: vP } = useScroll({ target: heroRef, offset: ["start end", "end start"] });
  const logoScale = useTransform(vP, [0, 0.45, 1], [0.85, 1.15, 0.9]);
  const logoRot = useTransform(vP, [0, 1], [-3, 4]);
  const titleLetterSpacing = useTransform(vP, [0, 0.5, 1], ["-0.02em", "-0.04em", "-0.045em"]);
  const titleY = useTransform(vP, [0, 1], [40, -60]);

  const featureColors = {
    0:"#89b4fa", 1:"#a6e3a1", 2:"#f38ba8", 3:"#74c7ec", 4:"#cba6f7", 5:"#fab387",
  };
  const accentColor = featureColors[activeFeature];

  return (
    <section id="varan" className="relative overflow-hidden bg-black">

      {/* ── Billboard ── */}
      <div ref={heroRef} className="varan-hero relative flex flex-col items-center justify-center overflow-hidden py-24 px-6 text-center min-h-screen md:py-36 lg:py-48">
        <div className="mesh-gradient mesh-gradient-varan" />
        <div className="varan-orb-1" /><div className="varan-orb-2" /><div className="varan-orb-3" />
        {[700, 980, 1280].map((sz, i) => (
          <div key={sz} className={`ring-anim ring-anim-${i}`} style={{ width:sz, height:sz }} />
        ))}
        <div className="spotlight-top" />

        <div className="relative z-10 max-w-5xl">
          <motion.p initial={{ opacity:0, y:16 }} animate={heroInView ? { opacity:1, y:0 } : {}}
            transition={{ duration:0.7, delay:0.1 }}
            className="mb-10 font-mono text-[11px] uppercase tracking-[0.4em] text-white/30">
            Flagship Project · v{VARAN.version} Beta
          </motion.p>

          <motion.div initial={{ opacity:0, scale:0.5 }} animate={heroInView ? { opacity:1, scale:1 } : {}}
            transition={{ duration:0.9, delay:0.15, ease:EASE }}
            style={{ scale: logoScale, rotate: logoRot }}
            className="mx-auto mb-12 flex justify-center">
            <div className="relative varan-logo-wrap">
              <div className="absolute -inset-8 rounded-3xl blur-3xl" style={{ background:"radial-gradient(circle, rgba(89,130,250,0.5), transparent 70%)" }} />
              <div className="varan-logo-ring" />
              <img src={LOGO} alt="Varan" className="relative h-24 w-24 rounded-3xl object-cover shadow-[0_0_60px_rgba(89,130,250,0.45)] md:h-28 md:w-28 md:shadow-[0_0_100px_rgba(89,130,250,0.5)]" />
            </div>
          </motion.div>

          <div className="overflow-hidden">
            <motion.h2 initial={{ y:"105%", opacity:0 }} animate={heroInView ? { y:0, opacity:1 } : {}}
              transition={{ duration:1.1, delay:0.25, ease:EASE }}
              style={{ letterSpacing: titleLetterSpacing, y: titleY }}
              className="varan-title font-semibold leading-none text-white">
              {"Varan".split("").map((c, i) => (
                <motion.span key={i}
                  initial={{ y:"105%", opacity:0 }}
                  animate={heroInView ? { y:0, opacity:1 } : {}}
                  transition={{ duration:1.2, delay:0.25 + i * 0.07, ease:EASE }}
                  className="inline-block varan-letter">
                  {c}
                </motion.span>
              ))}
            </motion.h2>
          </div>

          <motion.div initial={{ opacity:0, y:20 }} animate={heroInView ? { opacity:1, y:0 } : {}}
            transition={{ duration:0.8, delay:0.42 }}
            className="mt-6 mb-4 flex flex-wrap items-center justify-center gap-3">
            <Pill label="Beta" color="amber" pulse />
            <span className="h-px w-5 bg-white/15" />
            <span className="font-mono text-sm text-white/25">v0.3.0 · macOS · Windows · Linux</span>
          </motion.div>

          <motion.p initial={{ opacity:0, y:18 }} animate={heroInView ? { opacity:1, y:0 } : {}}
            transition={{ duration:0.8, delay:0.5 }}
            className="mt-8 text-[clamp(1.4rem,3vw,2rem)] font-light leading-relaxed text-white/50">
            {VARAN.tagline}
          </motion.p>
          <motion.p initial={{ opacity:0 }} animate={heroInView ? { opacity:1 } : {}}
            transition={{ duration:0.8, delay:0.58 }}
            className="mt-2 text-base text-white/28 max-w-xl mx-auto">{VARAN.sub}</motion.p>

          <motion.div initial={{ opacity:0, y:14 }} animate={heroInView ? { opacity:1, y:0 } : {}}
            transition={{ duration:0.8, delay:0.66 }} className="mt-10 mb-24">
            <p className="mx-auto mb-10 max-w-2xl text-base leading-8 text-white/38 md:text-lg">{VARAN.description}</p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Magnetic>
                <a href="https://forms.gle/gtygZ1q4TfqtXixT8"
                  className="group inline-flex items-center gap-2 rounded-full bg-blue-500 px-8 py-4 text-sm font-semibold text-white shadow-[0_0_60px_rgba(59,130,246,0.5)] transition-all hover:bg-blue-400 hover:scale-105 hover:shadow-[0_0_90px_rgba(59,130,246,0.7)]">
                  <Download className="h-3.5 w-3.5" /> Sign Up for Beta
                  <ArrowUpRight className="h-3.5 w-3.5 opacity-60 transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:opacity-100" />
                </a>
              </Magnetic>
              <Magnetic>
                <a href="https://docs.google.com/document/d/1fx3FBMWKLdiCfUIy3WdL9qJDT8GT5KlzzzNjqSNMuq0/edit?usp=sharing"
                  className="inline-flex items-center gap-2 rounded-full border border-white/[0.12] bg-white/[0.04] px-8 py-4 text-sm font-medium text-white/60 backdrop-blur-sm transition hover:bg-white/[0.09] hover:text-white">
                  <BookOpen className="h-3.5 w-3.5" /> Documentation
                </a>
              </Magnetic>
            </div>
            <div className="mt-8 flex items-center justify-center gap-2 text-xs text-white/30">
              <Sparkles className="h-3 w-3 text-blue-400" />
              <span>New in v{VARAN.version}: Insight BI, Time-Travel Rollback, 3× faster kernel</span>
            </div>
          </motion.div>
        </div>
      </div>

      {/* ── 6 Feature cards — horizontal scroll-snap gallery ── */}
      <div className="relative pb-28">
        <div className="mx-auto max-w-6xl px-6 md:px-10 lg:px-20">
          <Reveal><div className="mb-12 flex items-end justify-between gap-8 flex-wrap">
            <div>
              <Label>Six capabilities</Label>
              <h3 className="text-[clamp(2rem,4.5vw,3.5rem)] font-semibold tracking-[-0.03em] text-white leading-[1.05]">Engineered for<br /><span className="shimmer-text">every workflow.</span></h3>
            </div>
            <p className="max-w-sm text-sm leading-6 text-white/40">Swipe through. Each module is tightly integrated — they share tables, runtimes, and state.</p>
          </div></Reveal>
        </div>
        <Reveal delay={0.05}>
          <div className="feature-gallery">
            <div className="feature-gallery-track">
              <div className="feature-gallery-pad" />
              {VARAN.pillars.map(({ icon: Icon, title, desc }, i) => (
                <motion.div key={title} whileHover={{ y: -6 }} transition={{ duration: 0.25 }}
                  className="feature-card relative"
                  style={{ background:`linear-gradient(155deg, ${featureColors[i]}14 0%, rgba(255,255,255,0.018) 55%)`, borderColor: `${featureColors[i]}22` }}>
                  <div className="absolute -right-12 -top-12 h-40 w-40 rounded-full blur-3xl opacity-60"
                    style={{ background: `radial-gradient(circle, ${featureColors[i]}44, transparent 70%)` }} />
                  <div className="relative flex flex-col h-full">
                    <div className="mb-6 flex items-center justify-between">
                      <div className="flex h-12 w-12 items-center justify-center rounded-2xl backdrop-blur-md"
                        style={{ background:`${featureColors[i]}22`, border:`1px solid ${featureColors[i]}44` }}>
                        <Icon className="h-5 w-5" style={{ color: featureColors[i] }} />
                      </div>
                      <span className="font-mono text-[10px] tracking-widest text-white/25">0{i+1} / 06</span>
                    </div>
                    <h4 className="mb-3 text-2xl font-semibold text-white tracking-tight">{title}</h4>
                    <p className="text-sm leading-6 text-white/45 flex-1">{desc}</p>
                    <div className="mt-6 flex items-center gap-2 text-xs font-mono tracking-wider" style={{ color: featureColors[i] }}>
                      <span className="h-px flex-1" style={{ background: `linear-gradient(90deg, ${featureColors[i]}66, transparent)` }} />
                      Module
                    </div>
                  </div>
                </motion.div>
              ))}
              <div className="feature-gallery-pad" />
            </div>
          </div>
        </Reveal>
      </div>

      {/* ── Mockup on pedestal ── */}
      <div className="px-6 pb-28 md:px-10 lg:px-20">
        <div className="mx-auto max-w-6xl">
          <Reveal><div className="mb-10 text-center">
            <Label>Interface</Label>
            <h3 className="text-[clamp(2rem,4.5vw,3.5rem)] font-semibold tracking-tight text-white leading-tight">Built to feel fast.</h3>
            <p className="mt-3 text-white/30 text-base">Catppuccin Mocha. Everything in one window. No context switching.</p>
          </div></Reveal>
          <Reveal delay={0.1}>
            <div className="mockup-pedestal">
              <VaranMockup />
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-12 flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-[11px]" style={{ fontFamily:"'DM Mono',monospace", color: "rgba(255,255,255,0.3)" }}>
              <span className="flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-blue-400" /> DuckDB engine</span>
              <span className="flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-purple-400" /> Embedded Python 3.10</span>
              <span className="flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-rose-400" /> Automatic anomaly scans</span>
              <span className="flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-teal-400" /> Git-like lineage</span>
              <span className="flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-amber-400" /> Time-travel rollback</span>
            </div>
          </Reveal>
        </div>
      </div>

      {/* ── What's New in v0.1.3 ── */}
      <WhatsNew />

      {/* ── Manifesto pull-quote ── */}
      <Manifesto />

      {/* ── Interactive pillars ── */}
      <div className="px-6 py-24 md:px-10 lg:px-20 border-t border-white/[0.04]">
        <div className="mx-auto max-w-6xl">
          <Reveal>
            <Label>Deep dive</Label>
            <h3 className="mb-2 text-[clamp(2rem,4.5vw,3.5rem)] font-semibold tracking-tight text-white">Six pillars. Zero friction.</h3>
            <p className="mb-10 max-w-xl text-white/35 text-base">Each capability is tightly integrated. The whole is much more than the sum.</p>
          </Reveal>
          <Reveal delay={0.06}>
            <div className="mb-8 flex flex-wrap gap-2">
              {VARAN.pillars.map((f, i) => (
                <motion.button key={f.title} onClick={() => setActiveFeature(i)}
                  whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}
                  className="flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-medium transition-all"
                  style={{
                    borderColor: activeFeature===i ? `${featureColors[i]}55` : "rgba(255,255,255,0.07)",
                    background: activeFeature===i ? `${featureColors[i]}15` : "rgba(255,255,255,0.02)",
                    color: activeFeature===i ? featureColors[i] : "rgba(255,255,255,0.38)",
                    boxShadow: activeFeature===i ? `0 0 20px ${featureColors[i]}20` : "none",
                  }}>
                  <f.icon className="h-3.5 w-3.5" /> {f.title}
                </motion.button>
              ))}
            </div>
          </Reveal>
          <AnimatePresence mode="wait">
            <motion.div key={activeFeature}
              initial={{ opacity:0, y:16 }} animate={{ opacity:1, y:0 }} exit={{ opacity:0, y:-10 }}
              transition={{ duration:0.35, ease:EASE }}
              className="grid gap-8 lg:grid-cols-[1fr_1.15fr]">
              <div className="flex flex-col justify-center">
                <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl"
                  style={{ background:`${accentColor}18`, border:`1px solid ${accentColor}30` }}>
                  {(() => { const Icon = VARAN.pillars[activeFeature].icon; return <Icon className="h-6 w-6" style={{ color: accentColor }} />; })()}
                </div>
                <h4 className="mb-4 text-2xl font-semibold text-white">{VARAN.pillars[activeFeature].title}</h4>
                <p className="text-base leading-7 text-white/40">{VARAN.pillars[activeFeature].desc}</p>
              </div>
              <CodeBlock code={VARAN.pillars[activeFeature].code} lang={VARAN.pillars[activeFeature].lang} />
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {/* ── Workflow ── */}
      <div className="px-6 py-24 md:px-10 lg:px-20 border-t border-white/[0.04]">
        <div className="mx-auto max-w-6xl">
          <Reveal>
            <Label>How it works</Label>
            <h3 className="mb-14 text-[clamp(2rem,4.5vw,3.5rem)] font-semibold tracking-tight text-white">From zero to insight.</h3>
          </Reveal>
          <div className="relative">
            <div className="absolute left-5 top-0 bottom-0 hidden w-px bg-gradient-to-b from-blue-500/30 via-blue-500/10 to-transparent md:block" />
            <div className="space-y-4">
              {VARAN.workflow.map((step, i) => (
                <Reveal key={step.n} delay={i * 0.06}>
                  <motion.div whileHover={{ x:6 }} transition={{ duration:0.18 }} className="flex cursor-default items-start gap-6">
                    <div className="relative z-10 flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-blue-500/25 bg-blue-500/8">
                      <span className="font-mono text-xs font-semibold text-blue-400">{step.n}</span>
                    </div>
                    <motion.div whileHover={{ borderColor:"rgba(255,255,255,0.11)" }}
                      className="flex-1 rounded-2xl border border-white/[0.06] bg-white/[0.025] p-5 transition-colors">
                      <h4 className="mb-1.5 font-semibold text-white">{step.title}</h4>
                      <p className="text-sm leading-6 text-white/38">{step.desc}</p>
                    </motion.div>
                  </motion.div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── Before / After ── */}
      <div className="px-6 py-24 md:px-10 lg:px-20 border-t border-white/[0.04]">
        <div className="mx-auto max-w-6xl">
          <Reveal>
            <Label>Why Varan</Label>
            <h3 className="mb-3 text-[clamp(2rem,4.5vw,3.5rem)] font-semibold tracking-tight text-white">Stop writing boilerplate.</h3>
            <p className="mb-10 max-w-xl text-white/35 text-base">In typical workflows, most time goes to setup and plumbing. Varan removes all of it.</p>
          </Reveal>
          <Reveal delay={0.06}>
            {/* Desktop: 3-col table · Mobile: stacked cards */}
            <div className="hidden overflow-hidden rounded-2xl border border-white/[0.06] md:block">
              <div className="grid grid-cols-[1fr_1fr_1fr] border-b border-white/[0.06] bg-white/[0.02]">
                <div className="px-6 py-3.5 text-[11px] font-medium uppercase tracking-wider text-white/22">Task</div>
                <div className="border-l border-white/[0.04] px-6 py-3.5 text-[11px] font-medium uppercase tracking-wider text-red-400/50">Without</div>
                <div className="border-l border-white/[0.04] px-6 py-3.5 text-[11px] font-medium uppercase tracking-wider text-green-400/50">With Varan</div>
              </div>
              {VARAN.compare.map((row, i) => (
                <div key={i} className="grid grid-cols-[1fr_1fr_1fr] border-b border-white/[0.04] last:border-0 hover:bg-white/[0.015] transition-colors">
                  <div className="px-6 py-4 text-sm text-white/50">{row.task}</div>
                  <div className="border-l border-white/[0.04] px-6 py-4 font-mono text-[11px] leading-5 text-red-300/45">{row.before}</div>
                  <div className="border-l border-white/[0.04] px-6 py-4 font-mono text-[11px] leading-5 text-green-300/60">{row.after}</div>
                </div>
              ))}
            </div>
            <div className="space-y-3 md:hidden">
              {VARAN.compare.map((row, i) => (
                <div key={i} className="rounded-2xl border border-white/[0.06] p-4">
                  <div className="mb-3 text-sm font-medium text-white/70">{row.task}</div>
                  <div className="rounded-lg border border-red-400/15 bg-red-400/5 p-3">
                    <div className="mb-1 text-[9px] font-semibold uppercase tracking-widest text-red-400/60">Without</div>
                    <div className="font-mono text-[11px] leading-5 text-red-300/55 break-words">{row.before}</div>
                  </div>
                  <div className="mt-2 rounded-lg border border-green-400/15 bg-green-400/5 p-3">
                    <div className="mb-1 text-[9px] font-semibold uppercase tracking-widest text-green-400/60">With Varan</div>
                    <div className="font-mono text-[11px] leading-5 text-green-300/70 break-words">{row.after}</div>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </div>

      {/* ── Specs ── */}
      <div className="px-6 py-20 md:px-10 lg:px-20 border-t border-white/[0.04]">
        <div className="mx-auto max-w-6xl">
          <Reveal><Label>Tech specs</Label><h3 className="mb-10 text-2xl font-semibold text-white">What's inside.</h3></Reveal>
          <Reveal delay={0.04}>
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
              {VARAN.specs.map(spec => (
                <TiltCard key={spec.label}>
                  <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4 hover:border-white/10 transition-colors">
                    <div className="mb-1.5 font-mono text-[10px] uppercase tracking-widest text-white/18">{spec.label}</div>
                    <div className="text-sm font-medium text-white/65">{spec.value}</div>
                  </div>
                </TiltCard>
              ))}
            </div>
          </Reveal>
        </div>
      </div>

      {/* ── Proof numbers ── */}
      <ProofRow />

      {/* ── Quotes ── */}
      <Quotes />

      {/* ── CTA ── */}
      <Reveal>
        <div className="relative mx-6 mb-20 overflow-hidden rounded-3xl border border-white/[0.07] md:mx-10 lg:mx-20">
          <div className="cta-bg absolute inset-0" />
          <div className="relative px-8 py-16 text-center md:px-14 md:py-20">
            <img src={LOGO} alt="" className="mx-auto mb-6 h-14 w-14 rounded-2xl shadow-[0_0_50px_rgba(89,130,250,0.4)]" />
            <h3 className="mb-3 text-[clamp(1.8rem,4vw,3rem)] font-semibold text-white tracking-tight">Ready to try Varan?</h3>
            <p className="mx-auto mb-10 max-w-sm text-white/38">Beta is live. Join early, shape the product, and stop writing boilerplate forever.</p>
            <div className="flex flex-wrap items-center justify-center gap-3">
              <Magnetic>
                <a href="https://forms.gle/gtygZ1q4TfqtXixT8"
                  className="inline-flex items-center gap-2 rounded-full bg-white px-8 py-3.5 text-sm font-semibold text-black transition hover:scale-105 hover:shadow-[0_0_50px_rgba(255,255,255,0.25)]">
                  <Sparkles className="h-4 w-4" /> Request Beta Access
                </a>
              </Magnetic>
              <Magnetic>
                <a href="https://docs.google.com/document/d/1fx3FBMWKLdiCfUIy3WdL9qJDT8GT5KlzzzNjqSNMuq0/edit?usp=sharing"
                  className="inline-flex items-center gap-2 rounded-full border border-white/12 bg-white/[0.05] px-8 py-3.5 text-sm font-medium text-white/55 transition hover:bg-white/[0.1] hover:text-white">
                  View Documentation
                </a>
              </Magnetic>
            </div>
          </div>
        </div>
      </Reveal>
    </section>
  );
}

/* ══════════════════════════════════════════════════════
   PROJECTS
══════════════════════════════════════════════════════ */
function Projects() {
  const badgeColors = { "Senior Design":"blue", "Personal Project":"green", "Personal Project - In Progress":"amber", "In Progress":"amber" };
  const projectAccents = [
    { from:"#f38ba8", to:"#cba6f7" },  // Javeline - rose/mauve
    { from:"#89b4fa", to:"#94e2d5" },  // Smart Parking - blue/teal
    { from:"#fab387", to:"#f9e2af" },  // DineDash - peach/yellow
    { from:"#a6e3a1", to:"#74c7ec" },  // Jungle - green/sapphire
    { from:"#6c7086", to:"#313244" },  // Coming Soon - muted
  ];
  return (
    <section id="projects" className="bg-black px-6 py-28 md:px-10 lg:px-20">
      <div className="mx-auto max-w-6xl">
        <div className="mb-px h-px bg-gradient-to-r from-transparent via-white/[0.06] to-transparent" />
        <div className="pt-28">
          <Reveal>
            <Label>Other Work</Label>
            <h2 className="mb-4 text-[clamp(2.4rem,5vw,4rem)] font-semibold tracking-[-0.03em] text-white leading-[1.05]">
              More <span className="shimmer-text">projects</span>
            </h2>
            <p className="mb-14 max-w-xl text-white/40 text-base leading-7">
              A small sample of the ambitious things I've shipped — from full-stack frameworks to embedded systems to social platforms.
            </p>
          </Reveal>
          <div className="grid gap-5 md:grid-cols-2">
            {P.projects.map((proj, i) => {
              const accent = projectAccents[i] || projectAccents[0];
              return (
                <Reveal key={proj.name} delay={i * 0.07}>
                  <TiltCard className="h-full">
                    <motion.a href={proj.link}
                      whileHover={{ y: -4 }}
                      transition={{ duration: 0.25 }}
                      className="group relative block h-full overflow-hidden rounded-3xl border border-white/[0.07] bg-gradient-to-br from-white/[0.035] to-white/[0.008] p-8 transition-colors hover:border-white/[0.18]">
                      {/* Accent gradient glow */}
                      <div className="pointer-events-none absolute -right-24 -top-24 h-56 w-56 rounded-full blur-3xl opacity-30 transition-opacity duration-500 group-hover:opacity-80"
                        style={{ background: `radial-gradient(circle, ${accent.from} 0%, transparent 70%)` }} />
                      <div className="pointer-events-none absolute -left-20 -bottom-24 h-56 w-56 rounded-full blur-3xl opacity-20 transition-opacity duration-500 group-hover:opacity-50"
                        style={{ background: `radial-gradient(circle, ${accent.to} 0%, transparent 70%)` }} />
                      {/* Top-accent line that draws on hover */}
                      <span className="pointer-events-none absolute left-8 right-8 top-0 h-px origin-left scale-x-0 transition-transform duration-500 group-hover:scale-x-100"
                        style={{ background: `linear-gradient(90deg, transparent, ${accent.from}, ${accent.to}, transparent)` }} />
                      <div className="relative flex h-full flex-col">
                        <div className="mb-7 flex items-start justify-between">
                          <Pill label={proj.badge} color={badgeColors[proj.badge] || "slate"} />
                          <span className="flex h-9 w-9 items-center justify-center rounded-full border border-white/[0.08] bg-white/[0.04] text-white/25 transition-all duration-300 group-hover:rotate-0 group-hover:border-white/20 group-hover:bg-white/[0.1] group-hover:text-white"
                            style={{ transform: "rotate(-45deg)" }}>
                            <ArrowUpRight className="h-4 w-4" />
                          </span>
                        </div>
                        <h3 className="mb-3 text-2xl font-semibold text-white tracking-tight">{proj.name}</h3>
                        <p className="mb-7 text-sm leading-6 text-white/40 flex-1">{proj.desc}</p>
                        <div className="flex flex-wrap items-center gap-1.5">
                          {proj.stack.map(s => <Tag key={s}>{s}</Tag>)}
                        </div>
                      </div>
                    </motion.a>
                  </TiltCard>
                </Reveal>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════════════════════════════════
   AWARDS
══════════════════════════════════════════════════════ */
function Awards() {
  return (
    <section className="bg-black px-6 pb-28 md:px-10 lg:px-20">
      <div className="mx-auto max-w-6xl">
        <div className="mb-px h-px bg-gradient-to-r from-transparent via-white/[0.06] to-transparent" />
        <div className="pt-28">
          <Reveal><Label>Recognition</Label><h2 className="mb-14 text-[clamp(2.4rem,5vw,4rem)] font-semibold tracking-tight text-white">Awards</h2></Reveal>
          <div className="space-y-3">
            {P.awards.map((a, i) => (
              <Reveal key={a.title} delay={i * 0.08}>
                <TiltCard>
                  <motion.div whileHover={{ borderColor:"rgba(255,255,255,0.1)" }}
                    className="flex items-start gap-5 rounded-2xl border border-white/[0.06] bg-white/[0.025] p-6 transition-colors">
                    <div className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/[0.07] text-white/22">
                      <Award className="h-4 w-4" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-white/90">{a.title}</h4>
                      <p className="mt-0.5 font-mono text-[11px] uppercase tracking-wider text-white/18">{a.meta}</p>
                      <p className="mt-2.5 text-sm leading-6 text-white/35">{a.desc}</p>
                    </div>
                  </motion.div>
                </TiltCard>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════════════════════════════════
   CONTACT
══════════════════════════════════════════════════════ */
function CopyEmailButton() {
  const [copied, setCopied] = useState(false);
  const onCopy = e => {
    // allow meta/ctrl/right-click to still open mail client
    if (e.metaKey || e.ctrlKey) return;
    e.preventDefault();
    if (navigator.clipboard) navigator.clipboard.writeText(P.email);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  return (
    <Magnetic>
      <a href={`mailto:${P.email}`} onClick={onCopy}
        className="group relative inline-flex items-center gap-2 rounded-full bg-white px-9 py-4 text-sm font-semibold text-black shadow-[0_0_50px_rgba(255,255,255,0.12)] transition-all hover:shadow-[0_0_80px_rgba(255,255,255,0.25)] hover:scale-105">
        <AnimatePresence mode="wait">
          {copied ? (
            <motion.span key="c" initial={{ y:8, opacity:0 }} animate={{ y:0, opacity:1 }} exit={{ y:-8, opacity:0 }}
              transition={{ duration:0.25 }} className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-500" /> Email copied
            </motion.span>
          ) : (
            <motion.span key="e" initial={{ y:8, opacity:0 }} animate={{ y:0, opacity:1 }} exit={{ y:-8, opacity:0 }}
              transition={{ duration:0.25 }} className="flex items-center gap-2">
              <Mail className="h-4 w-4" /> Email me
              <ArrowUpRight className="h-3.5 w-3.5 opacity-25 transition group-hover:opacity-100 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </motion.span>
          )}
        </AnimatePresence>
      </a>
    </Magnetic>
  );
}

function Contact() {
  const socials = [
    { label:"GitHub", href:P.github, icon:<Github className="h-4 w-4" /> },
    { label:"LinkedIn", href:P.linkedin, icon:<svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg> },
    { label:"Instagram", href:P.instagram, icon:<svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/></svg> },
    { label:"Reddit", href:P.reddit, icon:<svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614a3.111 3.111 0 0 1 .042.52c0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701zM9.25 12C8.561 12 8 12.562 8 13.25c0 .687.561 1.248 1.25 1.248.687 0 1.248-.561 1.248-1.249 0-.688-.561-1.249-1.249-1.249zm5.5 0c-.687 0-1.248.561-1.248 1.25 0 .687.561 1.248 1.249 1.248.688 0 1.249-.561 1.249-1.249 0-.687-.562-1.249-1.25-1.249zm-5.466 3.99a.327.327 0 0 0-.231.094.33.33 0 0 0 0 .463c.842.842 2.484.913 2.961.913.477 0 2.105-.056 2.961-.913a.361.361 0 0 0 .029-.463.33.33 0 0 0-.464 0c-.547.533-1.684.73-2.512.73-.828 0-1.979-.196-2.512-.73a.326.326 0 0 0-.232-.095z"/></svg> },
  ];

  return (
    <section id="contact" className="relative overflow-hidden bg-black px-6 py-24 md:px-10 md:py-40 lg:px-20 lg:py-48">
      <div className="contact-orb-1" /><div className="contact-orb-2" />
      <Particles count={16} />
      <div className="relative mx-auto max-w-6xl text-center">
        <Reveal>
          <Label>Let's build together</Label>
          <h2 className="mb-6 text-[clamp(3rem,9vw,8rem)] font-semibold leading-[1.0] tracking-[-0.04em] text-white">
            Open to<br /><span className="shimmer-text">opportunities.</span>
          </h2>
          <p className="mx-auto mb-14 max-w-md text-base leading-7 text-white/35">
            If you're working on something ambitious — a product, a hard problem, or a team building tools people love — I'd love to connect.
          </p>
        </Reveal>
        <Reveal delay={0.1}>
          <div className="mb-10 flex flex-wrap items-center justify-center gap-3">
            <CopyEmailButton />
          </div>
          <div className="mb-10 font-mono text-xs text-white/25">
            <span className="select-all">{P.email}</span>
            <span className="mx-2 text-white/10">·</span>
            <span className="text-white/20">click Email me to copy</span>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3">
            {socials.map(s => (
              <Magnetic key={s.label}>
                <a href={s.href}
                  className="inline-flex items-center gap-2 rounded-full border border-white/[0.09] bg-white/[0.04] px-6 py-3 text-sm font-medium text-white/50 transition hover:bg-white/[0.09] hover:text-white">
                  {s.icon} {s.label}
                </a>
              </Magnetic>
            ))}
          </div>
        </Reveal>
        <div className="mt-28 border-t border-white/[0.05] pt-12">
          <div className="grid gap-10 text-left md:grid-cols-4">
            <div>
              <div className="flex items-center gap-2.5">
                <div className="flex h-8 w-8 items-center justify-center rounded-xl border border-white/10 bg-white/[0.04]">
                  <Layers3 className="h-4 w-4 text-white/40" />
                </div>
                <span className="text-sm font-medium text-white/60">{P.name.split(" ")[0]}</span>
              </div>
              <p className="mt-4 text-xs leading-5 text-white/30 max-w-xs">
                {P.sub} based in {P.location}. Available for ambitious product work.
              </p>
            </div>
            <div>
              <p className="mb-3 font-mono text-[10px] uppercase tracking-widest text-white/25">Explore</p>
              <ul className="space-y-2 text-xs text-white/45">
                {["About","Varan","Projects","Contact"].map(s => (
                  <li key={s}><a href={`#${s.toLowerCase()}`} className="transition hover:text-white">{s}</a></li>
                ))}
              </ul>
            </div>
            <div>
              <p className="mb-3 font-mono text-[10px] uppercase tracking-widest text-white/25">Varan</p>
              <ul className="space-y-2 text-xs text-white/45">
                <li><a href="https://forms.gle/gtygZ1q4TfqtXixT8" className="transition hover:text-white">Beta access</a></li>
                <li><a href="https://docs.google.com/document/d/1fx3FBMWKLdiCfUIy3WdL9qJDT8GT5KlzzzNjqSNMuq0/edit?usp=sharing" className="transition hover:text-white">Documentation</a></li>
                <li><span className="text-white/25">v{VARAN.version} Beta</span></li>
              </ul>
            </div>
            <div>
              <p className="mb-3 font-mono text-[10px] uppercase tracking-widest text-white/25">Elsewhere</p>
              <ul className="space-y-2 text-xs text-white/45">
                <li><a href={P.github} className="transition hover:text-white">GitHub</a></li>
                <li><a href={P.linkedin} className="transition hover:text-white">LinkedIn</a></li>
                <li><a href={P.instagram} className="transition hover:text-white">Instagram</a></li>
              </ul>
            </div>
          </div>
          <div className="mt-12 flex flex-wrap items-center justify-between gap-4 border-t border-white/[0.05] pt-6 text-[11px] text-white/20">
            <span className="font-mono">© 2026 · {P.name} · Built from scratch with care.</span>
            <div className="flex items-center gap-3 text-white/15 font-mono">
              <span>Baku, AZ</span><span>·</span>
              <span className="flex items-center gap-1.5"><span className="h-1.5 w-1.5 rounded-full bg-green-400 animate-pulse" /> Open to opportunities</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ══════════════════════════════════════════════════════
   APP
══════════════════════════════════════════════════════ */
export default function App() {
  return (
    <div className="min-h-screen bg-black text-white antialiased selection:bg-blue-500/25 selection:text-white">
      <style>{`
        *, *::before, *::after { box-sizing: border-box; margin: 0; }
        html { scroll-behavior: smooth; font-family: -apple-system, BlinkMacSystemFont, 'SF Pro Display', 'DM Sans', ui-sans-serif, system-ui, sans-serif; }
        body { background: #000; overflow-x: hidden; }
        [class*="font-mono"], code { font-family: 'SF Mono', 'DM Mono', ui-monospace, monospace !important; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: #000; }
        ::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.07); border-radius: 999px; }
        ::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.12); }

        /* ── Shimmer gradient text ── */
        .shimmer-text {
          background: linear-gradient(100deg, #fff 0%, #93c5fd 28%, #818cf8 50%, #93c5fd 72%, #fff 100%);
          background-size: 250% auto;
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
          animation: text-shimmer 5s linear infinite;
        }
        @keyframes text-shimmer {
          from { background-position: 200% center; }
          to   { background-position: -200% center; }
        }

        /* ── Hero name size ── */
        .hero-name { font-size: clamp(5rem, 12vw, 11rem); }

        /* ── Varan title size ── */
        .varan-title { font-size: clamp(6.5rem, 18vw, 16rem); }

        /* ── Hero background layers ── */
        .hero-section { isolation: isolate; }
        .hero-orb-1 {
          position: absolute; width: 900px; height: 900px;
          left: 50%; top: 50%; transform: translate(-50%, -55%);
          border-radius: 50%;
          background: radial-gradient(circle, rgba(59,130,246,0.14) 0%, transparent 65%);
          pointer-events: none; will-change: opacity;
          animation: orb-opacity 8s ease-in-out infinite;
        }
        .hero-orb-2 {
          position: absolute; width: 600px; height: 600px;
          left: 20%; top: 30%; transform: translate(-50%, -50%);
          border-radius: 50%;
          background: radial-gradient(circle, rgba(99,102,241,0.1) 0%, transparent 65%);
          pointer-events: none; will-change: opacity;
          animation: orb-opacity 11s ease-in-out infinite reverse;
        }
        .hero-orb-3 {
          position: absolute; width: 500px; height: 500px;
          right: 15%; top: 35%; transform: translate(50%, -50%);
          border-radius: 50%;
          background: radial-gradient(circle, rgba(14,165,233,0.08) 0%, transparent 65%);
          pointer-events: none; will-change: opacity;
          animation: orb-opacity 9s ease-in-out infinite 2s;
        }
        /* Animate opacity only — no filter/scale — keeps GPU happy */
        @keyframes orb-opacity {
          0%, 100% { opacity: 0.75; }
          50%       { opacity: 1; }
        }

        /* ── Perspective grid ── */
        .hero-grid {
          position: absolute; inset: 0; pointer-events: none; z-index: 0;
          background-image:
            linear-gradient(rgba(89,130,250,0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(89,130,250,0.04) 1px, transparent 1px);
          background-size: 80px 80px;
          transform: perspective(700px) rotateX(22deg) scale(1.6) translateY(8%);
          transform-origin: 50% 85%;
          mask-image: radial-gradient(ellipse 80% 55% at 50% 95%, black 20%, transparent 68%);
          animation: grid-fade 14s ease-in-out infinite alternate;
        }
        @keyframes grid-fade {
          from { opacity: 0.45; }
          to   { opacity: 0.85; }
        }

        /* ── Avatar ring ── */
        .avatar-ring {
          position: absolute; inset: -4px;
          border-radius: 50%;
          background: conic-gradient(from 0deg, #3b82f6, #818cf8, #38bdf8, #3b82f6);
          animation: ring-spin 4s linear infinite;
          z-index: 0;
        }
        @keyframes ring-spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }

        /* ── Particles ── */
        @keyframes particle-float {
          0%, 100% { transform: translateY(0px) scale(1); }
          50%       { transform: translateY(-28px) scale(1.3); }
        }

        /* ── Varan hero ── */
        .varan-hero { background: #000; }
        .varan-orb-1 {
          position: absolute; width: 1100px; height: 1100px;
          left: 50%; top: 50%; transform: translate(-50%, -52%);
          border-radius: 50%;
          background: radial-gradient(circle, rgba(29,78,216,0.22) 0%, transparent 60%);
          pointer-events: none; will-change: opacity;
          animation: orb-opacity 10s ease-in-out infinite;
        }
        .varan-orb-2 {
          position: absolute; width: 700px; height: 700px;
          left: 18%; top: 35%;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(55,48,163,0.16) 0%, transparent 60%);
          pointer-events: none; will-change: opacity;
          animation: orb-opacity 13s ease-in-out infinite reverse;
        }
        .varan-orb-3 {
          position: absolute; width: 600px; height: 600px;
          right: 12%; top: 40%;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(3,105,161,0.14) 0%, transparent 60%);
          pointer-events: none; will-change: opacity;
          animation: orb-opacity 9s ease-in-out infinite 3s;
        }

        /* ── Rings ── */
        .ring-anim {
          position: absolute; left: 50%; top: 50%;
          transform: translate(-50%, -50%);
          border-radius: 50%;
          border: 1px solid rgba(89,130,250,0.06);
          animation: ring-pulse 7s ease-in-out infinite;
          pointer-events: none;
          will-change: transform, opacity;
        }
        .ring-anim-0 { animation-duration: 5s; }
        .ring-anim-1 { animation-duration: 7.5s; animation-delay: 1.6s; }
        .ring-anim-2 { animation-duration: 10s; animation-delay: 3.2s; }
        @keyframes ring-pulse {
          0%, 100% { transform: translate(-50%,-50%) scale(1); opacity: 0.4; }
          50%       { transform: translate(-50%,-50%) scale(1.04); opacity: 0.9; }
        }

        /* ── Varan mockup body height ── */
        .varan-body { height: 440px; }
        @media (max-width: 768px) {
          .varan-body { height: 420px; }
        }

        /* ── Mockup on pedestal ── */
        .mockup-pedestal {
          position: relative;
          filter: drop-shadow(0 60px 120px rgba(0,0,0,0.9));
        }
        .mockup-pedestal::before {
          content: '';
          position: absolute;
          bottom: -40px; left: 50%; transform: translateX(-50%);
          width: 70%; height: 80px;
          background: radial-gradient(ellipse, rgba(89,130,250,0.22) 0%, transparent 70%);
          filter: blur(20px);
          pointer-events: none;
        }

        /* ── CTA background ── */
        .cta-bg {
          background: linear-gradient(135deg,
            rgba(59,130,246,0.22) 0%,
            rgba(99,102,241,0.16) 50%,
            rgba(14,165,233,0.20) 100%);
        }

        /* ── Contact orbs ── */
        .contact-orb-1 {
          position: absolute; width: 700px; height: 700px;
          left: 10%; top: 20%; transform: translate(-50%, -50%);
          border-radius: 50%;
          background: radial-gradient(circle, rgba(30,64,175,0.16) 0%, transparent 60%);
          pointer-events: none; will-change: opacity;
          animation: orb-opacity 12s ease-in-out infinite;
        }
        .contact-orb-2 {
          position: absolute; width: 600px; height: 600px;
          right: 8%; top: 35%;
          border-radius: 50%;
          background: radial-gradient(circle, rgba(49,46,129,0.14) 0%, transparent 60%);
          pointer-events: none; will-change: opacity;
          animation: orb-opacity 9s ease-in-out infinite reverse;
        }

        /* ── Ticker ── */
        .ticker-track {
          animation: ticker-move 35s linear infinite;
          width: max-content;
        }
        @keyframes ticker-move {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }

        /* ── Scroll caret ── */
        .scroll-caret { animation: caret-bounce 2s ease-in-out infinite; }
        @keyframes caret-bounce {
          0%, 100% { transform: translateY(0); }
          50%       { transform: translateY(6px); }
        }

        /* ── Mesh gradient background (static — cheap) ── */
        .mesh-gradient {
          position: absolute; inset: 0; z-index: 0;
          pointer-events: none;
          opacity: 0.65;
        }
        .mesh-gradient-hero {
          background:
            radial-gradient(at 22% 18%, rgba(59,130,246,0.22) 0, transparent 42%),
            radial-gradient(at 80% 28%, rgba(129,140,248,0.16) 0, transparent 42%),
            radial-gradient(at 68% 78%, rgba(14,165,233,0.16) 0, transparent 42%),
            radial-gradient(at 18% 82%, rgba(99,102,241,0.14) 0, transparent 42%);
        }
        .mesh-gradient-varan {
          background:
            radial-gradient(at 30% 22%, rgba(29,78,216,0.28) 0, transparent 45%),
            radial-gradient(at 75% 28%, rgba(139,92,246,0.18) 0, transparent 45%),
            radial-gradient(at 70% 80%, rgba(14,165,233,0.2) 0, transparent 45%),
            radial-gradient(at 25% 72%, rgba(99,102,241,0.16) 0, transparent 45%);
        }

        /* ── Spotlight top (Apple keynote-style) ── */
        .spotlight-top {
          position: absolute; top: 0; left: 50%; transform: translateX(-50%);
          width: 1400px; height: 500px;
          background: radial-gradient(ellipse at center top, rgba(255,255,255,0.07) 0%, transparent 55%);
          pointer-events: none; z-index: 0;
        }

        /* ── Varan logo ring (rotating gradient) ── */
        .varan-logo-wrap { position: relative; }
        .varan-logo-ring {
          position: absolute; inset: -10px;
          border-radius: 28px;
          background: conic-gradient(from 0deg,
            rgba(59,130,246,0.9),
            rgba(129,140,248,0.9),
            rgba(56,189,248,0.9),
            rgba(59,130,246,0.9));
          animation: ring-spin 8s linear infinite;
          filter: blur(2px);
          opacity: 0.8;
          z-index: 0;
          mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
          -webkit-mask: linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0);
          -webkit-mask-composite: xor;
          mask-composite: exclude;
          padding: 1.5px;
        }

        /* ── Feature gallery (horizontal scroll) ── */
        .feature-gallery {
          overflow-x: auto;
          overflow-y: hidden;
          scrollbar-width: none;
          -ms-overflow-style: none;
          scroll-snap-type: x mandatory;
          scroll-padding: 10vw;
        }
        .feature-gallery::-webkit-scrollbar { display: none; }
        .feature-gallery-track {
          display: flex;
          gap: 20px;
          padding: 8px 0 40px;
        }
        .feature-gallery-pad {
          flex: 0 0 calc((100vw - min(72rem, 100vw)) / 2 + 1.5rem);
          min-width: 1.5rem;
        }
        .feature-card {
          flex: 0 0 min(85vw, 380px);
          min-height: 320px;
          border-radius: 24px;
          border: 1px solid rgba(255,255,255,0.07);
          padding: 28px;
          scroll-snap-align: center;
          backdrop-filter: blur(10px);
          transition: border-color 0.3s, transform 0.3s;
        }

        /* ── Manifesto aura (static — no blur animation) ── */
        .manifesto-aura {
          position: absolute; inset: 0; pointer-events: none; z-index: 0;
          background:
            radial-gradient(ellipse 70% 50% at 50% 40%, rgba(59,130,246,0.15), transparent 70%),
            radial-gradient(ellipse 50% 40% at 20% 70%, rgba(129,140,248,0.1), transparent 70%),
            radial-gradient(ellipse 40% 30% at 85% 25%, rgba(56,189,248,0.11), transparent 70%);
          opacity: 0.85;
        }

        /* ── Hide scrollbar (for tab strip in mockup) ── */
        .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
        .scrollbar-hide::-webkit-scrollbar { display: none; }

        /* ── Mobile — strip expensive effects ── */
        @media (max-width: 768px) {
          .hero-grid,
          .mesh-gradient,
          .varan-orb-2, .varan-orb-3,
          .contact-orb-2,
          .ring-anim-1, .ring-anim-2,
          .spotlight-top,
          .varan-logo-ring {
            display: none !important;
          }
          .hero-orb-1, .hero-orb-2, .hero-orb-3,
          .varan-orb-1,
          .contact-orb-1 {
            animation: none !important;
            transform: none !important;
          }
          .hero-name { font-size: clamp(2.8rem, 14vw, 4.5rem) !important; }
          .varan-title { font-size: clamp(3.5rem, 18vw, 6rem) !important; letter-spacing: -0.03em !important; }
          .ticker-track { animation-duration: 50s; }
          .feature-gallery-pad { flex-basis: 1rem !important; }
          .feature-card {
            flex-basis: min(82vw, 320px) !important;
            min-height: 280px !important;
            padding: 22px !important;
          }
          .mockup-pedestal::before { display: none; }
        }
        /* Reduced-motion — kill all looping animations */
        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after {
            animation-duration: 0.001s !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.001s !important;
            scroll-behavior: auto !important;
          }
        }
      `}</style>
      <ScrollProgress />
      <MouseGlow />
      <Nav />
      <Hero />
      <Ticker />
      <About />
      <VaranSection />
      <Projects />
      <Awards />
      <Contact />
    </div>
  );
}
